import React from 'react';
import { AssistantAiMuteAdvancedSettingsModal } from './AssistantAiMuteAdvancedSettingsModal';
import { AssistantCommentReplyModePanel } from './AssistantCommentReplyModePanel';
import { AssistantVoiceSettingsPanel } from './AssistantVoiceSettingsPanel';
import { AiTokenBudgetPanel } from '../../ai-usage/components/AiTokenBudgetPanel';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Breadcrumb, BreadcrumbItem, Spinner, LocalizedDateInput, LocalizedDateTimeInput } from '../../../shared/components';
import { TimeInput24 } from '../../../shared/components/TimeInput24';
import { LocationMapPicker } from '../../../shared/components';
import {
  formatDateTimeInputValue,
  formatLocalizedDateTimeLabel,
  parseDateTimeInputValue,
  toDateInputValue,
} from '../../../shared/utils/localization';
import { normalizeTimeInputValue } from '../../../shared/utils/timeInput';
import DepartmentModal from './DepartmentModal';
import PositionModal from './PositionModal';
import DepartmentTreeList from './DepartmentTreeList';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import {
  BookingPreferenceAppliesTo,
  BookingPreferenceRuleMode,
  ChatBotBlockout,
  CompanyClosure,
  CompanyOpeningHour,
  Company,
  Department,
  Position,
  CreateChatBotBlockoutRequest,
  CreateCompanyClosureRequest,
  CreateCompanyOpeningHourRequest,
  CreateCompanyRequest,
  CreateDepartmentRequest,
  CreatePositionRequest,
  CreateLocationRequest,
  CreateRoomGroupRequest,
  CreateRoomRequest,
  Location,
  Room,
  RoomGroup,
  ScheduleRepeatPattern,
  UpdateAiAssistantSettingsRequest,
  UpdateChatBotBlockoutRequest,
  UpdateRoomRequest,
  type AiAssistantSettings,
} from '../types/Company';
import {
  createChatBotBlockout,
  createClosure,
  createOpeningHour,
  createRoom,
  createRoomGroup,
  createCompany,
  createDepartment,
  createPosition,
  createLocation,
  deleteChatBotBlockout,
  deleteClosure,
  deleteOpeningHour,
  deleteRoom,
  deleteRoomGroup,
  deleteDepartment,
  deletePosition,
  deleteLocation,
  listClosures,
  listChatBotBlockouts,
  listCompanies,
  listDepartments,
  listPositions,
  listLocations,
  listOpeningHours,
  listRoomGroups,
  listRooms,
  updateChatBotBlockout,
  updateClosure,
  updateOpeningHour,
  updateRoom,
  updateRoomGroup,
  updateCompany,
  updateDepartment,
  updatePosition,
  updateLocation,
  getAiAssistantSettings,
  updateAiAssistantSettings,
} from '../services/companyService';
import { listAppointmentTypes, type AppointmentType } from '../../calendar';

type CompanyPageTab = 'company' | 'departments' | 'positions' | 'locations' | 'rooms' | 'business-hours' | 'chatbot';

const emptyCompanyForm: CreateCompanyRequest = {
  name: '',
  mailerFrom: '',
  email: '',
  phone: '',
  city: '',
  country: '',
  language: '',
  isActive: true,
};

const emptyLocationForm: CreateLocationRequest = {
  name: '',
  code: '',
  email: '',
  phone: '',
  city: '',
  country: '',
  latitude: undefined,
  longitude: undefined,
  checkInRadiusMeters: undefined,
  isPrimary: false,
  isActive: true,
};

const finiteNumber = (value?: number): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const emptyDepartmentForm: CreateDepartmentRequest = {
  name: '',
  parentDepartmentId: '',
  isActive: true,
};

const emptyPositionForm: CreatePositionRequest = {
  name: '',
  departmentId: '',
};

const emptyRoomGroupForm: CreateRoomGroupRequest = {
  name: '',
  description: '',
  isActive: true,
};

const emptyRoomForm: CreateRoomRequest = {
  name: '',
  code: '',
  description: '',
  capacity: undefined,
  isActive: true,
};

const emptyOpeningHourForm: CreateCompanyOpeningHourRequest = {
  repeatPattern: 'weekly',
  isAllDay: false,
  daysOfWeek: [1, 2, 3, 4, 5],
  startTime: '09:00:00',
  endTime: '17:00:00',
  isEnabled: true,
};

const emptyClosureForm: CreateCompanyClosureRequest = {
  repeatPattern: 'none',
  isAllDay: false,
  startAtUtc: undefined,
  endAtUtc: undefined,
  isEnabled: true,
};

const emptyChatBotBlockoutForm: CreateChatBotBlockoutRequest = {
  name: '',
  description: '',
  appointmentTypeId: undefined,
  mode: 'never',
  appliesTo: 'aiAssistant',
  repeatPattern: 'weekly',
  isAllDay: false,
  daysOfWeek: [1, 2, 3, 4, 5],
  startTime: '09:00:00',
  endTime: '17:00:00',
  roomIds: [],
  isEnabled: true,
};


type ChatBotRoomCatalogItem = {
  id: string;
  roomName: string;
  roomGroupName: string;
  locationName: string;
};

const utcToLocalDateTimeInput = (utc?: string): string => {
  if (!utc) {
    return '';
  }

  const date = new Date(utc);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return formatDateTimeInputValue(date);
};

const localDateTimeInputToUtc = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = parseDateTimeInputValue(value);
  if (!parsed) {
    return undefined;
  }

  return parsed.toISOString();
};

const formatBusinessTimeRangeLabel = (startTime?: string, endTime?: string): string => {
  const start = normalizeTimeInputValue(startTime || '');
  const end = normalizeTimeInputValue(endTime || '');
  if (!start || !end) {
    return '-';
  }

  return `${start} - ${end}`;
};

type ChatBotScheduleFieldsProps = {
  draft: CreateChatBotBlockoutRequest;
  onDraftChange: (updater: (prev: CreateChatBotBlockoutRequest) => CreateChatBotBlockoutRequest) => void;
  disabled?: boolean;
};

const ChatBotScheduleFields: React.FC<ChatBotScheduleFieldsProps> = ({
  draft,
  onDraftChange,
  disabled = false,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
    <TimeInput24
      className="text-input"
      value={(draft.startTime || '').slice(0, 5)}
      disabled={disabled || Boolean(draft.isAllDay)}
      onChange={(startTime) => onDraftChange(prev => ({
        ...prev,
        startTime: startTime ? `${startTime}:00` : undefined,
      }))}
    />
    <span aria-hidden="true">-</span>
    <TimeInput24
      className="text-input"
      value={(draft.endTime || '').slice(0, 5)}
      disabled={disabled || Boolean(draft.isAllDay)}
      onChange={(endTime) => onDraftChange(prev => ({
        ...prev,
        endTime: endTime ? `${endTime}:00` : undefined,
      }))}
    />
  </div>
);

const toOpeningHourForm = (openingHour: CompanyOpeningHour): CreateCompanyOpeningHourRequest => ({
  locationId: openingHour.locationId,
  repeatPattern: openingHour.repeatPattern,
  isAllDay: openingHour.isAllDay,
  daysOfWeek: openingHour.daysOfWeek,
  startDate: openingHour.startDate,
  endDate: openingHour.endDate,
  startTime: openingHour.startTime,
  endTime: openingHour.endTime,
  isEnabled: openingHour.isEnabled,
});

const toClosureForm = (closure: CompanyClosure): CreateCompanyClosureRequest => ({
  locationId: closure.locationId,
  repeatPattern: closure.repeatPattern,
  isAllDay: closure.isAllDay,
  startAtUtc: closure.startAtUtc,
  endAtUtc: closure.endAtUtc,
  isEnabled: closure.isEnabled,
});

const toCompanyForm = (company: Company): CreateCompanyRequest => ({
  name: company.name,
  mailerFrom: company.mailerFrom || '',
  email: company.email || '',
  phone: company.phone || '',
  city: company.city || '',
  country: company.country || '',
  language: company.language || '',
  isActive: company.isActive,
});

const toLocationForm = (location: Location): CreateLocationRequest => ({
  name: location.name,
  code: location.code || '',
  email: location.email || '',
  phone: location.phone || '',
  city: location.city || '',
  country: location.country || '',
  latitude: location.latitude,
  longitude: location.longitude,
  checkInRadiusMeters: location.checkInRadiusMeters,
  isPrimary: location.isPrimary,
  isActive: location.isActive,
});

const toDepartmentForm = (department: Department): CreateDepartmentRequest => ({
  name: department.name,
  parentDepartmentId: department.parentDepartmentId || '',
  isActive: department.isActive,
});

const toPositionForm = (position: Position): CreatePositionRequest => ({
  name: position.name,
  departmentId: position.departmentId || '',
});

const buildDepartmentLabelMap = (departments: Department[]): Map<string, string> => {
  const byId = new Map(departments.map(item => [item.id, item]));
  const labels = new Map<string, string>();

  const buildLabel = (departmentId: string, visited = new Set<string>()): string => {
    const cached = labels.get(departmentId);
    if (cached) {
      return cached;
    }

    const current = byId.get(departmentId);
    if (!current) {
      return '';
    }

    if (visited.has(departmentId)) {
      return current.name;
    }

    visited.add(departmentId);
    const parentLabel = current.parentDepartmentId ? buildLabel(current.parentDepartmentId, visited) : '';
    const label = parentLabel ? `${parentLabel} / ${current.name}` : current.name;
    labels.set(departmentId, label);
    return label;
  };

  departments.forEach(department => {
    buildLabel(department.id);
  });

  return labels;
};

const toRoomGroupForm = (roomGroup: RoomGroup): CreateRoomGroupRequest => ({
  name: roomGroup.name,
  description: roomGroup.description || '',
  isActive: roomGroup.isActive,
});

const toRoomForm = (room: Room): CreateRoomRequest => ({
  name: room.name,
  code: room.code || '',
  description: room.description || '',
  capacity: room.capacity,
  isActive: room.isActive,
});

const toChatBotBlockoutForm = (blockout: ChatBotBlockout): CreateChatBotBlockoutRequest => ({
  name: blockout.name,
  description: blockout.description || '',
  appointmentTypeId: blockout.appointmentTypeId,
  mode: blockout.mode,
  appliesTo: blockout.appliesTo,
  locationId: blockout.locationId,
  repeatPattern: blockout.repeatPattern,
  isAllDay: blockout.isAllDay,
  daysOfWeek: blockout.daysOfWeek,
  startDate: blockout.startDate,
  endDate: blockout.endDate,
  startTime: blockout.startTime,
  endTime: blockout.endTime,
  roomIds: [...blockout.roomIds],
  isEnabled: blockout.isEnabled,
});

const normalizeChatBotDraftDays = (days?: number[]): number[] => Array.from(new Set(days || [])).sort((left, right) => left - right);
const normalizeChatBotDraftRoomIds = (roomIds?: string[]): string[] => Array.from(new Set((roomIds || []).filter(Boolean))).sort();

const toComparableChatBotDraft = (draft: CreateChatBotBlockoutRequest) => ({
  name: (draft.name || '').trim(),
  description: (draft.description || '').trim(),
  appointmentTypeId: draft.appointmentTypeId || '',
  mode: draft.mode || 'never',
  appliesTo: draft.appliesTo || 'aiAssistant',
  locationId: draft.locationId || '',
  repeatPattern: draft.repeatPattern || 'weekly',
  isAllDay: Boolean(draft.isAllDay),
  daysOfWeek: normalizeChatBotDraftDays(draft.daysOfWeek),
  startDate: draft.startDate || '',
  endDate: draft.endDate || '',
  startTime: draft.startTime || '',
  endTime: draft.endTime || '',
  roomIds: normalizeChatBotDraftRoomIds(draft.roomIds),
  isEnabled: draft.isEnabled ?? true,
});

const areChatBotDraftsEqual = (left: CreateChatBotBlockoutRequest, right: CreateChatBotBlockoutRequest): boolean => {
  const normalizedLeft = toComparableChatBotDraft(left);
  const normalizedRight = toComparableChatBotDraft(right);

  return normalizedLeft.name === normalizedRight.name
    && normalizedLeft.description === normalizedRight.description
    && normalizedLeft.appointmentTypeId === normalizedRight.appointmentTypeId
    && normalizedLeft.mode === normalizedRight.mode
    && normalizedLeft.appliesTo === normalizedRight.appliesTo
    && normalizedLeft.locationId === normalizedRight.locationId
    && normalizedLeft.repeatPattern === normalizedRight.repeatPattern
    && normalizedLeft.isAllDay === normalizedRight.isAllDay
    && normalizedLeft.startDate === normalizedRight.startDate
    && normalizedLeft.endDate === normalizedRight.endDate
    && normalizedLeft.startTime === normalizedRight.startTime
    && normalizedLeft.endTime === normalizedRight.endTime
    && normalizedLeft.isEnabled === normalizedRight.isEnabled
    && normalizedLeft.daysOfWeek.length === normalizedRight.daysOfWeek.length
    && normalizedLeft.daysOfWeek.every((day, index) => day === normalizedRight.daysOfWeek[index])
    && normalizedLeft.roomIds.length === normalizedRight.roomIds.length
    && normalizedLeft.roomIds.every((roomId, index) => roomId === normalizedRight.roomIds[index]);
};

const hasChatBotRowChanges = (blockout: ChatBotBlockout, draft: CreateChatBotBlockoutRequest): boolean => {
  const original = toChatBotBlockoutForm(blockout);
  return !areChatBotDraftsEqual(original, draft);
};

const CompanyTabReadOnlyBanner: React.FC = () => {
  const { t } = useTranslation('companies');

  return (
    <div className="company-tab-readonly-hint" role="status">
      {t('companyTabReadOnlyHint')}
    </div>
  );
};

export const CompaniesPage: React.FC = () => {
  const { t } = useTranslation(['companies', 'admin', 'contacts', 'common']);
  const location = useLocation();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManageCompany = hasPermission(PermissionScopes.companiesCompanyManage);
  const canManageDepartments = hasPermission(PermissionScopes.companiesDepartmentsManage);
  const canManagePositions = hasPermission(PermissionScopes.companiesPositionsManage);
  const canManageLocations = hasPermission(PermissionScopes.companiesLocationsManage);
  const canManageRooms = hasPermission(PermissionScopes.companiesRoomsManage);
  const canManageBusinessHours = hasPermission(PermissionScopes.companiesBusinessHoursManage);
  const canManageChatbotBlockouts = hasPermission(PermissionScopes.companiesChatbotBlockoutsManage);
  const canManageAssistantSettings = hasPermission(PermissionScopes.companiesAssistantManage);
  const canManageTokenBudget = hasPermission(PermissionCodes.adminAccess);
  const hasBroadCompanyAccess = hasPermission([
    PermissionCodes.companiesRead,
    PermissionCodes.companiesManage,
  ]);
  const allCompanyPageTabs = React.useMemo<Array<{ id: CompanyPageTab; label: string }>>(() => ([
    { id: 'company', label: 'companyTab' },
    { id: 'departments', label: 'departmentsTab' },
    { id: 'positions', label: 'positionsTab' },
    { id: 'locations', label: 'locationsTab' },
    { id: 'rooms', label: 'roomsTab' },
    { id: 'business-hours', label: 'businessHoursTab' },
    { id: 'chatbot', label: 'chatbotTab' },
  ]), []);
  const companyTabVisibility = React.useMemo<Record<CompanyPageTab, boolean>>(() => ({
    company: hasBroadCompanyAccess || canManageCompany,
    departments: hasBroadCompanyAccess || canManageDepartments,
    positions: hasBroadCompanyAccess || canManagePositions,
    locations: hasBroadCompanyAccess || canManageLocations,
    rooms: hasBroadCompanyAccess || canManageRooms,
    'business-hours': hasBroadCompanyAccess || canManageBusinessHours,
    chatbot: hasBroadCompanyAccess || canManageChatbotBlockouts || canManageAssistantSettings,
  }), [
    canManageAssistantSettings,
    canManageBusinessHours,
    canManageChatbotBlockouts,
    canManageCompany,
    canManageDepartments,
    canManageLocations,
    canManagePositions,
    canManageRooms,
    hasBroadCompanyAccess,
  ]);
  const visibleCompanyPageTabs = React.useMemo(
    () => allCompanyPageTabs.filter(tab => companyTabVisibility[tab.id]),
    [allCompanyPageTabs, companyTabVisibility],
  );
  const dayOptions = React.useMemo<Array<{ value: number; label: string }>>(() => ([
    { value: 1, label: t('mon', { ns: 'companies' }) },
    { value: 2, label: t('tue', { ns: 'companies' }) },
    { value: 3, label: t('wed', { ns: 'companies' }) },
    { value: 4, label: t('thu', { ns: 'companies' }) },
    { value: 5, label: t('fri', { ns: 'companies' }) },
    { value: 6, label: t('sat', { ns: 'companies' }) },
    { value: 0, label: t('sun', { ns: 'companies' }) },
  ]), [t]);
  const repeatPatternOptions = React.useMemo<Array<{ value: ScheduleRepeatPattern; label: string }>>(() => ([
    { value: 'none', label: t('repeatNone', { ns: 'companies' }) },
    { value: 'weekly', label: t('repeatWeekly', { ns: 'companies' }) },
    { value: 'monthly', label: t('repeatMonthly', { ns: 'companies' }) },
    { value: 'yearly', label: t('repeatYearly', { ns: 'companies' }) },
  ]), [t]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(true);
  const [loadingDepartments, setLoadingDepartments] = React.useState(false);
  const [loadingPositions, setLoadingPositions] = React.useState(false);
  const [loadingLocations, setLoadingLocations] = React.useState(false);
  const [loadingRoomGroups, setLoadingRoomGroups] = React.useState(false);
  const [loadingRooms, setLoadingRooms] = React.useState(false);
  const [savingCompany, setSavingCompany] = React.useState(false);
  const [savingPosition, setSavingPosition] = React.useState(false);
  const [savingLocation, setSavingLocation] = React.useState(false);
  const [savingRoomGroup, setSavingRoomGroup] = React.useState(false);
  const [savingRoom, setSavingRoom] = React.useState(false);
  const [loadingOpeningHours, setLoadingOpeningHours] = React.useState(false);
  const [loadingClosures, setLoadingClosures] = React.useState(false);
  const [savingOpeningHour, setSavingOpeningHour] = React.useState(false);
  const [savingClosure, setSavingClosure] = React.useState(false);
  const [loadingChatBotBlockouts, setLoadingChatBotBlockouts] = React.useState(false);
  const [, setLoadingChatBotRooms] = React.useState(false);
  const [savingChatBotBlockout, setSavingChatBotBlockout] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState<CompanyPageTab>('company');

  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = React.useState<string>('');
  const [editingCompanyId, setEditingCompanyId] = React.useState<string>('');
  const [showCompanyForm, setShowCompanyForm] = React.useState(false);
  const [companyForm, setCompanyForm] = React.useState<CreateCompanyRequest>(emptyCompanyForm);
  const [companyError, setCompanyError] = React.useState<string | null>(null);

  const [departments, setDepartments] = React.useState<Department[]>([]);
  const [editingDepartmentId, setEditingDepartmentId] = React.useState<string>('');
  const [showDepartmentForm, setShowDepartmentForm] = React.useState(false);
  const [departmentForm, setDepartmentForm] = React.useState<CreateDepartmentRequest>(emptyDepartmentForm);
  const [departmentError, setDepartmentError] = React.useState<string | null>(null);
  const [savingDepartment, setSavingDepartment] = React.useState(false);

  const [positions, setPositions] = React.useState<Position[]>([]);
  const [editingPositionId, setEditingPositionId] = React.useState<string>('');
  const [showPositionForm, setShowPositionForm] = React.useState(false);
  const [positionForm, setPositionForm] = React.useState<CreatePositionRequest>(emptyPositionForm);
  const [positionError, setPositionError] = React.useState<string | null>(null);

  const [locations, setLocations] = React.useState<Location[]>([]);
  const [editingLocationId, setEditingLocationId] = React.useState<string>('');
  const [showLocationForm, setShowLocationForm] = React.useState(false);
  const [locationForm, setLocationForm] = React.useState<CreateLocationRequest>(emptyLocationForm);
  const [locationError, setLocationError] = React.useState<string | null>(null);

  const [roomGroups, setRoomGroups] = React.useState<RoomGroup[]>([]);
  const [rooms, setRooms] = React.useState<Room[]>([]);
  const [selectedLocationIdForRooms, setSelectedLocationIdForRooms] = React.useState<string>('');
  const [selectedRoomGroupId, setSelectedRoomGroupId] = React.useState<string>('');
  const [editingRoomGroupId, setEditingRoomGroupId] = React.useState<string>('');
  const [editingRoomId, setEditingRoomId] = React.useState<string>('');
  const [showRoomGroupForm, setShowRoomGroupForm] = React.useState(false);
  const [showRoomForm, setShowRoomForm] = React.useState(false);
  const [roomGroupForm, setRoomGroupForm] = React.useState<CreateRoomGroupRequest>(emptyRoomGroupForm);
  const [roomForm, setRoomForm] = React.useState<CreateRoomRequest>(emptyRoomForm);
  const [roomFormRoomGroupId, setRoomFormRoomGroupId] = React.useState<string>('');
  const [roomGroupError, setRoomGroupError] = React.useState<string | null>(null);
  const [roomError, setRoomError] = React.useState<string | null>(null);
  const [openingHourError, setOpeningHourError] = React.useState<string | null>(null);
  const [closureError, setClosureError] = React.useState<string | null>(null);

  const [openingHours, setOpeningHours] = React.useState<CompanyOpeningHour[]>([]);
  const [closures, setClosures] = React.useState<CompanyClosure[]>([]);
  const [editingOpeningHourId, setEditingOpeningHourId] = React.useState<string>('');
  const [editingClosureId, setEditingClosureId] = React.useState<string>('');
  const [showOpeningHourForm, setShowOpeningHourForm] = React.useState(false);
  const [showClosureForm, setShowClosureForm] = React.useState(false);
  const [openingHourForm, setOpeningHourForm] = React.useState<CreateCompanyOpeningHourRequest>(emptyOpeningHourForm);
  const [closureForm, setClosureForm] = React.useState<CreateCompanyClosureRequest>(emptyClosureForm);
  const [chatBotBlockouts, setChatBotBlockouts] = React.useState<ChatBotBlockout[]>([]);
  const [chatBotDraftById, setChatBotDraftById] = React.useState<Record<string, CreateChatBotBlockoutRequest>>({});
  const [chatBotRoomPickerByBlockout, setChatBotRoomPickerByBlockout] = React.useState<Record<string, string>>({});
  const [chatBotRoomCatalog, setChatBotRoomCatalog] = React.useState<ChatBotRoomCatalogItem[]>([]);
  const [chatBotAppointmentTypes, setChatBotAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [newChatBotBlockoutForm, setNewChatBotBlockoutForm] = React.useState<CreateChatBotBlockoutRequest>(emptyChatBotBlockoutForm);
  const [newChatBotRoomPicker, setNewChatBotRoomPicker] = React.useState('');
  const [chatBotError, setChatBotError] = React.useState<string | null>(null);

  const [loadingAssistantSettings, setLoadingAssistantSettings] = React.useState(false);
  const [savingAssistantSettings, setSavingAssistantSettings] = React.useState(false);
  const [assistantSettingsForm, setAssistantSettingsForm] = React.useState<UpdateAiAssistantSettingsRequest>({});
  const [assistantCommentReplySettings, setAssistantCommentReplySettings] = React.useState<
    Pick<AiAssistantSettings, 'aiAssistantCommentReplyModeByChannel'>
  >({ aiAssistantCommentReplyModeByChannel: {} });
  const [assistantSettingsError, setAssistantSettingsError] = React.useState<string | null>(null);
  const [assistantSettingsNotice, setAssistantSettingsNotice] = React.useState<string | null>(null);
  const [assistantMuteAdvancedOpen, setAssistantMuteAdvancedOpen] = React.useState(false);
  const selectedCompany = React.useMemo(
    () => companies.find(company => company.id === selectedCompanyId) || null,
    [companies, selectedCompanyId],
  );

  const selectedRoomGroup = React.useMemo(
    () => roomGroups.find(roomGroup => roomGroup.id === selectedRoomGroupId) || null,
    [roomGroups, selectedRoomGroupId],
  );
  const departmentLabelById = React.useMemo(() => buildDepartmentLabelMap(departments), [departments]);
  const parentDepartmentOptions = React.useMemo(
    () => departments
      .filter(department => department.id !== editingDepartmentId)
      .map(department => ({ id: department.id, label: departmentLabelById.get(department.id) || department.name }))
      .sort((left, right) => left.label.localeCompare(right.label)),
    [departments, departmentLabelById, editingDepartmentId],
  );
  const positionDepartmentOptions = React.useMemo(
    () => departments
      .map(department => ({ id: department.id, label: departmentLabelById.get(department.id) || department.name }))
      .sort((left, right) => left.label.localeCompare(right.label)),
    [departmentLabelById, departments],
  );

  const canCreateCompany = companies.length === 0 && canManageCompany;


  const breadcrumbItems: BreadcrumbItem[] = React.useMemo(() => {
    const items: BreadcrumbItem[] = [
      { label: t('admin', { ns: 'admin' }), path: '/admin' },
      { label: t('company', { ns: 'companies' }), path: '/admin/companies' },
    ];

    if (selectedCompany) {
      items.push({ label: selectedCompany.name, path: '/admin/companies' });
    }

    return items;
  }, [selectedCompany, t]);

  const loadCompanies = React.useCallback(async () => {
    setLoadingCompanies(true);
    setCompanyError(null);

    try {
      const data = await listCompanies({
        sortBy: 'name',
        sortDir: 'asc',
        page: 1,
        pageSize: 100,
      });
      setCompanies(data);
    } catch (error) {
      setCompanyError(error instanceof Error ? error.message : t('failedToLoadCompanies', { ns: 'companies' }));
    } finally {
      setLoadingCompanies(false);
    }
  }, [t]);


  const loadAssistantSettings = React.useCallback(async () => {
    if (!canManageAssistantSettings) {
      return;
    }

    setLoadingAssistantSettings(true);
    setAssistantSettingsError(null);
    setAssistantSettingsNotice(null);

    try {
      const assistantData = await getAiAssistantSettings();

      setAssistantSettingsForm({
        displayName: assistantData.displayName || '',
        autoRefreshAiMemoryMessageThreshold: assistantData.isAutoRefreshAiMemoryMessageThresholdOverridden
          ? assistantData.autoRefreshAiMemoryMessageThreshold
          : undefined,
        isBackgroundMemoryRefreshEnabled: assistantData.isBackgroundMemoryRefreshEnabledOverridden
          ? assistantData.isBackgroundMemoryRefreshEnabled
          : undefined,
        aiGeneratedMessageDisclaimer: assistantData.aiGeneratedMessageDisclaimer || '',
        showEmployeeNameOnExternalOutbound: assistantData.showEmployeeNameOnExternalOutbound,
      });
      setAssistantCommentReplySettings({
        aiAssistantCommentReplyModeByChannel: assistantData.aiAssistantCommentReplyModeByChannel,
      });
    } catch (error) {
      setAssistantSettingsError(error instanceof Error ? error.message : t('failedToLoadAssistantSettings', { ns: 'companies' }));
    } finally {
      setLoadingAssistantSettings(false);
    }
  }, [canManageAssistantSettings, t]);

  React.useEffect(() => {
    if (companies.length === 0) {
      setSelectedCompanyId('');
      return;
    }

    const selectedStillExists = companies.some(company => company.id === selectedCompanyId);
    const fallbackCompanyId = selectedStillExists ? selectedCompanyId : companies[0].id;
    setSelectedCompanyId(fallbackCompanyId);
  }, [companies, selectedCompanyId]);

  const loadLocationsForCompany = React.useCallback(async (companyId: string) => {
    if (!companyId) {
      setLocations([]);
      return;
    }

    setLoadingLocations(true);
    setLocationError(null);

    try {
      const data = await listLocations({
        sortBy: 'name',
        sortDir: 'asc',
        page: 1,
        pageSize: 200,
      });
      setLocations(data);
    } catch (error) {
      setLocationError(error instanceof Error ? error.message : t('failedToLoadLocations', { ns: 'companies' }));
    } finally {
      setLoadingLocations(false);
    }
  }, [t]);

  const loadDepartmentsForCompany = React.useCallback(async (companyId: string) => {
    if (!companyId) {
      setDepartments([]);
      return;
    }

    setLoadingDepartments(true);
    setDepartmentError(null);

    try {
      const data = await listDepartments();
      setDepartments(data);
    } catch (error) {
      setDepartmentError(error instanceof Error ? error.message : t('failedToLoadDepartments', { ns: 'companies' }));
    } finally {
      setLoadingDepartments(false);
    }
  }, [t]);

  const loadPositionsForCompany = React.useCallback(async (companyId: string) => {
    if (!companyId) {
      setPositions([]);
      return;
    }

    setLoadingPositions(true);
    setPositionError(null);

    try {
      const [departmentData, positionData] = await Promise.all([
        listDepartments(),
        listPositions(),
      ]);

      setDepartments(departmentData);
      setPositions(positionData);
    } catch (error) {
      setPositionError(error instanceof Error ? error.message : t('failedToLoadPositions', { ns: 'companies' }));
    } finally {
      setLoadingPositions(false);
    }
  }, [t]);

  const loadRoomGroupsForLocation = React.useCallback(async (companyId: string, locationId: string) => {
    if (!companyId || !locationId) {
      setRoomGroups([]);
      setSelectedRoomGroupId('');
      return;
    }

    setLoadingRoomGroups(true);
    setRoomGroupError(null);

    try {
      const data = await listRoomGroups(locationId, {
        sortBy: 'name',
        sortDir: 'asc',
        page: 1,
        pageSize: 200,
      });
      setRoomGroups(data);
    } catch (error) {
      setRoomGroupError(error instanceof Error ? error.message : t('failedToLoadRoomGroups', { ns: 'companies' }));
    } finally {
      setLoadingRoomGroups(false);
    }
  }, [t]);

  const loadRoomsForRoomGroup = React.useCallback(async (companyId: string, locationId: string, roomGroupId: string) => {
    if (!companyId || !locationId || !roomGroupId) {
      setRooms([]);
      return;
    }

    setLoadingRooms(true);
    setRoomError(null);

    try {
      const data = await listRooms(locationId, roomGroupId, {
        sortBy: 'name',
        sortDir: 'asc',
        page: 1,
        pageSize: 200,
      });
      setRooms(data);
    } catch (error) {
      setRoomError(error instanceof Error ? error.message : t('failedToLoadRooms', { ns: 'companies' }));
    } finally {
      setLoadingRooms(false);
    }
  }, [t]);

  const loadOpeningHoursForCompany = React.useCallback(async (companyId: string) => {
    if (!companyId) {
      setOpeningHours([]);
      return;
    }

    setLoadingOpeningHours(true);
    setOpeningHourError(null);

    try {
      const data = await listOpeningHours();
      setOpeningHours(data);
    } catch (error) {
      setOpeningHourError(error instanceof Error ? error.message : t('failedToLoadOpeningHours', { ns: 'companies' }));
    } finally {
      setLoadingOpeningHours(false);
    }
  }, [t]);

  const loadClosuresForCompany = React.useCallback(async (companyId: string) => {
    if (!companyId) {
      setClosures([]);
      return;
    }

    setLoadingClosures(true);
    setClosureError(null);

    try {
      const data = await listClosures();
      setClosures(data);
    } catch (error) {
      setClosureError(error instanceof Error ? error.message : t('failedToLoadClosures', { ns: 'companies' }));
    } finally {
      setLoadingClosures(false);
    }
  }, [t]);

  const loadChatBotBlockoutsForCompany = React.useCallback(async (companyId: string) => {
    if (!companyId) {
      setChatBotBlockouts([]);
      return;
    }

    setLoadingChatBotBlockouts(true);
    setChatBotError(null);

    try {
      const data = await listChatBotBlockouts({
        sortBy: 'name',
        sortDir: 'asc',
        page: 1,
        pageSize: 200,
      });
      setChatBotBlockouts(data);
    } catch (error) {
      setChatBotError(error instanceof Error ? error.message : t('failedToLoadChatbotBlockouts', { ns: 'companies' }));
    } finally {
      setLoadingChatBotBlockouts(false);
    }
  }, [t]);

  const loadChatBotRoomCatalogForCompany = React.useCallback(async (companyId: string) => {
    if (!companyId) {
      setChatBotRoomCatalog([]);
      return;
    }

    setLoadingChatBotRooms(true);

    try {
      const companyLocations = await listLocations({
        sortBy: 'name',
        sortDir: 'asc',
        page: 1,
        pageSize: 200,
      });

      const roomCatalogById = new Map<string, ChatBotRoomCatalogItem>();
      for (const location of companyLocations) {
        const locationRoomGroups = await listRoomGroups(location.id, {
          sortBy: 'name',
          sortDir: 'asc',
          page: 1,
          pageSize: 200,
        });

        for (const roomGroup of locationRoomGroups) {
          const groupRooms = await listRooms(location.id, roomGroup.id, {
            sortBy: 'name',
            sortDir: 'asc',
            page: 1,
            pageSize: 200,
          });

          groupRooms.forEach(room => {
            roomCatalogById.set(room.id, {
              id: room.id,
              roomName: room.name,
              roomGroupName: roomGroup.name,
              locationName: location.name,
            });
          });
        }
      }

      setChatBotRoomCatalog(Array.from(roomCatalogById.values()));
    } catch {
      setChatBotRoomCatalog([]);
    } finally {
      setLoadingChatBotRooms(false);
    }
  }, []);

  const removeChatBotBlockout = async (blockoutId: string) => {
    if (!selectedCompanyId || !window.confirm(t('confirmDeleteChatbotBlockout', { ns: 'companies' }))) {
      return;
    }

    setSavingChatBotBlockout(true);
    setChatBotError(null);

    try {
      await deleteChatBotBlockout(blockoutId);
      await loadChatBotBlockoutsForCompany(selectedCompanyId);
    } catch (error) {
      setChatBotError(error instanceof Error ? error.message : t('failedToDeleteChatbotBlockout', { ns: 'companies' }));
    } finally {
      setSavingChatBotBlockout(false);
    }
  };

  const setChatBotDraft = (blockoutId: string, updater: (prev: CreateChatBotBlockoutRequest) => CreateChatBotBlockoutRequest) => {
    setChatBotDraftById(prev => ({
      ...prev,
      [blockoutId]: updater(prev[blockoutId] || emptyChatBotBlockoutForm),
    }));
  };

  const saveInlineChatBotBlockout = async (blockoutId: string) => {
    const draft = chatBotDraftById[blockoutId];
    if (!draft) {
      return;
    }

    const name = (draft.name || '').trim();
    if (!name) {
      setChatBotError(t('blockoutNameRequired', { ns: 'companies' }));
      return;
    }

    const hasRecurring = Boolean(draft.daysOfWeek?.length) && Boolean(draft.startTime) && Boolean(draft.endTime);
    if (!hasRecurring) {
      setChatBotError(t('blockoutRecurringRequired', { ns: 'companies' }));
      return;
    }

    const payload: UpdateChatBotBlockoutRequest = {
      ...draft,
      name,
      description: (draft.description || '').trim() || undefined,
      mode: (draft.mode || 'never') as BookingPreferenceRuleMode,
      appliesTo: (draft.appliesTo || 'aiAssistant') as BookingPreferenceAppliesTo,
      roomIds: normalizeChatBotDraftRoomIds(draft.roomIds),
    };

    if (!draft.appointmentTypeId) {
      payload.clearAppointmentTypeId = true;
      delete payload.appointmentTypeId;
    }

    setSavingChatBotBlockout(true);
    setChatBotError(null);
    try {
      await updateChatBotBlockout(blockoutId, payload);
      await loadChatBotBlockoutsForCompany(selectedCompanyId);
    } catch (error) {
      setChatBotError(error instanceof Error ? error.message : t('failedToUpdateChatbotBlockoutRow', { ns: 'companies' }));
    } finally {
      setSavingChatBotBlockout(false);
    }
  };

  const createInlineChatBotBlockout = async () => {
    const name = (newChatBotBlockoutForm.name || '').trim();
    if (!name) {
      setChatBotError(t('blockoutNameRequired', { ns: 'companies' }));
      return;
    }

    const hasRecurring = Boolean(newChatBotBlockoutForm.daysOfWeek?.length)
      && Boolean(newChatBotBlockoutForm.startTime)
      && Boolean(newChatBotBlockoutForm.endTime);
    if (!hasRecurring) {
      setChatBotError(t('newBlockoutRecurringRequired', { ns: 'companies' }));
      return;
    }

    setSavingChatBotBlockout(true);
    setChatBotError(null);
    try {
      await createChatBotBlockout({
        ...newChatBotBlockoutForm,
        name,
        description: (newChatBotBlockoutForm.description || '').trim() || undefined,
        mode: (newChatBotBlockoutForm.mode || 'never') as BookingPreferenceRuleMode,
        appliesTo: (newChatBotBlockoutForm.appliesTo || 'aiAssistant') as BookingPreferenceAppliesTo,
        roomIds: normalizeChatBotDraftRoomIds(newChatBotBlockoutForm.roomIds),
        appointmentTypeId: newChatBotBlockoutForm.appointmentTypeId || undefined,
      });
      setNewChatBotBlockoutForm(emptyChatBotBlockoutForm);
      setNewChatBotRoomPicker('');
      await loadChatBotBlockoutsForCompany(selectedCompanyId);
    } catch (error) {
      setChatBotError(error instanceof Error ? error.message : t('failedToCreateChatbotBlockoutRow', { ns: 'companies' }));
    } finally {
      setSavingChatBotBlockout(false);
    }
  };

  const addRoomToInlineBlockoutDraft = (blockoutId: string) => {
    const roomId = chatBotRoomPickerByBlockout[blockoutId];
    if (!roomId) {
      return;
    }

    setChatBotDraft(blockoutId, prev => ({
      ...prev,
      roomIds: normalizeChatBotDraftRoomIds([...(prev.roomIds || []), roomId]),
    }));
    setChatBotRoomPickerByBlockout(prev => ({ ...prev, [blockoutId]: '' }));
  };

  const removeRoomFromInlineBlockoutDraft = (blockoutId: string, roomId: string) => {
    setChatBotDraft(blockoutId, prev => ({
      ...prev,
      roomIds: normalizeChatBotDraftRoomIds((prev.roomIds || []).filter(id => id !== roomId)),
    }));
  };

  const addRoomToNewBlockoutDraft = () => {
    if (!newChatBotRoomPicker) {
      return;
    }

    setNewChatBotBlockoutForm(prev => ({
      ...prev,
      roomIds: normalizeChatBotDraftRoomIds([...(prev.roomIds || []), newChatBotRoomPicker]),
    }));
    setNewChatBotRoomPicker('');
  };

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadCompanies();
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [loadCompanies]);

  React.useEffect(() => {
    if (activeTab !== 'departments') {
      return;
    }

    void loadDepartmentsForCompany(selectedCompanyId);
  }, [activeTab, loadDepartmentsForCompany, selectedCompanyId]);

  React.useEffect(() => {
    if (activeTab !== 'positions') {
      return;
    }

    void loadPositionsForCompany(selectedCompanyId);
  }, [activeTab, loadPositionsForCompany, selectedCompanyId]);

  React.useEffect(() => {
    void loadLocationsForCompany(selectedCompanyId);
  }, [loadLocationsForCompany, selectedCompanyId]);

  React.useEffect(() => {
    if (!selectedCompanyId || locations.length === 0) {
      setSelectedLocationIdForRooms('');
      setRoomGroups([]);
      setRooms([]);
      return;
    }

    const exists = locations.some(location => location.id === selectedLocationIdForRooms);
    if (exists) {
      return;
    }

    setSelectedLocationIdForRooms(locations[0].id);
  }, [locations, selectedCompanyId, selectedLocationIdForRooms]);

  React.useEffect(() => {
    if (activeTab !== 'rooms') {
      return;
    }

    void loadRoomGroupsForLocation(selectedCompanyId, selectedLocationIdForRooms);
  }, [activeTab, loadRoomGroupsForLocation, selectedCompanyId, selectedLocationIdForRooms]);

  React.useEffect(() => {
    if (roomGroups.length === 0) {
      setSelectedRoomGroupId('');
      setRooms([]);
      return;
    }

    const exists = roomGroups.some(roomGroup => roomGroup.id === selectedRoomGroupId);
    if (exists) {
      return;
    }

    setSelectedRoomGroupId(roomGroups[0].id);
  }, [roomGroups, selectedRoomGroupId]);

  React.useEffect(() => {
    if (roomGroups.length === 0) {
      setRoomFormRoomGroupId('');
      return;
    }

    const exists = roomGroups.some(roomGroup => roomGroup.id === roomFormRoomGroupId);
    if (exists) {
      return;
    }

    setRoomFormRoomGroupId(selectedRoomGroupId || roomGroups[0].id);
  }, [roomFormRoomGroupId, roomGroups, selectedRoomGroupId]);

  React.useEffect(() => {
    if (activeTab !== 'rooms') {
      return;
    }

    void loadRoomsForRoomGroup(selectedCompanyId, selectedLocationIdForRooms, selectedRoomGroupId);
  }, [activeTab, loadRoomsForRoomGroup, selectedCompanyId, selectedLocationIdForRooms, selectedRoomGroupId]);

  React.useEffect(() => {
    if (activeTab !== 'business-hours') {
      return;
    }

    void loadOpeningHoursForCompany(selectedCompanyId);
    void loadClosuresForCompany(selectedCompanyId);
  }, [activeTab, loadClosuresForCompany, loadOpeningHoursForCompany, selectedCompanyId]);

  React.useEffect(() => {
    if (activeTab !== 'chatbot') {
      return;
    }

    void loadChatBotBlockoutsForCompany(selectedCompanyId);
    void loadChatBotRoomCatalogForCompany(selectedCompanyId);
  }, [activeTab, loadChatBotBlockoutsForCompany, loadChatBotRoomCatalogForCompany, selectedCompanyId]);

  React.useEffect(() => {
    if (chatBotBlockouts.length === 0) {
      setChatBotDraftById({});
      setChatBotRoomPickerByBlockout({});
      return;
    }

    setChatBotDraftById(prev => {
      const next: Record<string, CreateChatBotBlockoutRequest> = {};
      chatBotBlockouts.forEach(blockout => {
        next[blockout.id] = prev[blockout.id] || toChatBotBlockoutForm(blockout);
      });
      return next;
    });

    setChatBotRoomPickerByBlockout(prev => {
      const next: Record<string, string> = {};
      chatBotBlockouts.forEach(blockout => {
        next[blockout.id] = prev[blockout.id] || '';
      });
      return next;
    });
  }, [chatBotBlockouts]);

  React.useEffect(() => {
    if (activeTab !== 'chatbot') {
      return;
    }

    void (async () => {
      try {
        const types = await listAppointmentTypes({ status: 'active' });
        setChatBotAppointmentTypes(types);
      } catch {
        setChatBotAppointmentTypes([]);
      }
    })();
  }, [activeTab]);


  React.useEffect(() => {
    if (activeTab !== 'chatbot') {
      return;
    }

    void loadAssistantSettings();
  }, [activeTab, loadAssistantSettings]);

  const startCreateCompany = () => {
    if (!canCreateCompany) {
      setCompanyError(t('onlyOneCompanyAllowedEditExisting', { ns: 'companies' }));
      return;
    }

    setEditingCompanyId('');
    setCompanyForm(emptyCompanyForm);
    setCompanyError(null);
    setShowCompanyForm(true);
  };

  const startEditCompany = (company: Company) => {
    setEditingCompanyId(company.id);
    setCompanyForm(toCompanyForm(company));
    setCompanyError(null);
    setShowCompanyForm(true);
  };


  const saveAssistantSettings = async () => {
    setSavingAssistantSettings(true);
    setAssistantSettingsError(null);
    setAssistantSettingsNotice(null);

    try {
      const updated = await updateAiAssistantSettings({
        displayName: assistantSettingsForm.displayName?.trim() || undefined,
        autoRefreshAiMemoryMessageThreshold: assistantSettingsForm.autoRefreshAiMemoryMessageThreshold,
        isBackgroundMemoryRefreshEnabled: assistantSettingsForm.isBackgroundMemoryRefreshEnabled,
        aiGeneratedMessageDisclaimer: assistantSettingsForm.aiGeneratedMessageDisclaimer?.trim() || undefined,
        showEmployeeNameOnExternalOutbound: assistantSettingsForm.showEmployeeNameOnExternalOutbound ?? false,
      });

      setAssistantSettingsForm({
        displayName: updated.displayName || '',
        autoRefreshAiMemoryMessageThreshold: updated.isAutoRefreshAiMemoryMessageThresholdOverridden
          ? updated.autoRefreshAiMemoryMessageThreshold
          : undefined,
        isBackgroundMemoryRefreshEnabled: updated.isBackgroundMemoryRefreshEnabledOverridden
          ? updated.isBackgroundMemoryRefreshEnabled
          : undefined,
        aiGeneratedMessageDisclaimer: updated.aiGeneratedMessageDisclaimer || '',
        showEmployeeNameOnExternalOutbound: updated.showEmployeeNameOnExternalOutbound,
      });
      setAssistantSettingsNotice(t('assistantSettingsSaved', { ns: 'companies' }));
      showToast(t('assistantSettingsSaved', { ns: 'companies' }), 'success');
    } catch (error) {
      setAssistantSettingsError(error instanceof Error ? error.message : t('failedToSaveAssistantSettings', { ns: 'companies' }));
      showToast(t('failedToSaveAssistantSettings', { ns: 'companies' }), 'error');
    } finally {
      setSavingAssistantSettings(false);
    }
  };


  const cancelEditCompany = () => {
    setEditingCompanyId('');
    setCompanyError(null);
    setShowCompanyForm(false);
    if (selectedCompany) {
      setCompanyForm(toCompanyForm(selectedCompany));
      return;
    }

    setCompanyForm(emptyCompanyForm);
  };

  const saveCompany = async () => {
    const name = (companyForm.name || '').trim();
    if (!name) {
      setCompanyError(t('companyNameRequired', { ns: 'companies' }));
      return;
    }

    setSavingCompany(true);
    setCompanyError(null);

    try {
      const payload: CreateCompanyRequest = {
        ...companyForm,
        name,
        mailerFrom: (companyForm.mailerFrom || '').trim() || undefined,
        email: (companyForm.email || '').trim() || undefined,
        phone: (companyForm.phone || '').trim() || undefined,
        city: (companyForm.city || '').trim() || undefined,
        country: (companyForm.country || '').trim() || undefined,
        language: (companyForm.language || '').trim() || undefined,
      };

      if (editingCompanyId) {
        await updateCompany(payload);
      } else {
        if (!canCreateCompany) {
          setCompanyError(t('onlyOneCompanyAllowed', { ns: 'companies' }));
          return;
        }

        const created = await createCompany(payload);
        if (created?.id) {
          setSelectedCompanyId(created.id);
        }
      }

      await loadCompanies();
      setEditingCompanyId('');
      setShowCompanyForm(false);
      setCompanyForm(emptyCompanyForm);
    } catch (error) {
      setCompanyError(error instanceof Error ? error.message : t('failedToSaveCompany', { ns: 'companies' }));
    } finally {
      setSavingCompany(false);
    }
  };

  const startCreateLocation = () => {
    setEditingLocationId('');
    setLocationForm(emptyLocationForm);
    setLocationError(null);
    setShowLocationForm(true);
  };

  const startCreateDepartment = () => {
    setEditingDepartmentId('');
    setDepartmentForm(emptyDepartmentForm);
    setDepartmentError(null);
    setShowDepartmentForm(true);
  };

  const startCreatePosition = () => {
    setEditingPositionId('');
    setPositionForm(emptyPositionForm);
    setPositionError(null);
    setShowPositionForm(true);
  };

  const startEditPosition = (position: Position) => {
    setEditingPositionId(position.id);
    setPositionForm(toPositionForm(position));
    setPositionError(null);
    setShowPositionForm(true);
  };

  const cancelPositionForm = React.useCallback(() => {
    setEditingPositionId('');
    setPositionForm(emptyPositionForm);
    setPositionError(null);
    setShowPositionForm(false);
  }, []);

  const startEditDepartment = (department: Department) => {
    setEditingDepartmentId(department.id);
    setDepartmentForm(toDepartmentForm(department));
    setDepartmentError(null);
    setShowDepartmentForm(true);
  };

  const cancelDepartmentForm = React.useCallback(() => {
    setEditingDepartmentId('');
    setDepartmentForm(emptyDepartmentForm);
    setDepartmentError(null);
    setShowDepartmentForm(false);
  }, []);

  const closeOrganizationEditorForms = React.useCallback(() => {
    cancelDepartmentForm();
    cancelPositionForm();
  }, [cancelDepartmentForm, cancelPositionForm]);

  const handleActiveTabChange = (tab: CompanyPageTab) => {
    if (tab !== activeTab) {
      closeOrganizationEditorForms();
    }
    setActiveTab(tab);
  };

  React.useEffect(() => {
    if (visibleCompanyPageTabs.length === 0) {
      return;
    }

    if (!visibleCompanyPageTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(visibleCompanyPageTabs[0].id);
    }
  }, [activeTab, visibleCompanyPageTabs]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (!tab) {
      return;
    }

    const matched = visibleCompanyPageTabs.find(item => item.id === tab);
    if (matched) {
      setActiveTab(matched.id);
    }
  }, [location.search, visibleCompanyPageTabs]);

  React.useEffect(() => () => {
    document.body.style.overflow = '';
  }, []);

  React.useEffect(() => {
    closeOrganizationEditorForms();
  }, [location.pathname, closeOrganizationEditorForms]);

  const saveDepartment = async () => {
    if (!selectedCompanyId) {
      setDepartmentError(t('selectCompanyFirst', { ns: 'companies' }));
      return;
    }

    const name = (departmentForm.name || '').trim();
    if (!name) {
      setDepartmentError(t('departmentNameRequired', { ns: 'companies' }));
      return;
    }

    if (name.length > 150) {
      setDepartmentError(t('departmentNameMaxLength', { ns: 'companies' }));
      return;
    }

    setSavingDepartment(true);
    setDepartmentError(null);

    try {
      const payload: CreateDepartmentRequest = {
        name,
        parentDepartmentId: (departmentForm.parentDepartmentId || '').trim() || undefined,
        isActive: departmentForm.isActive ?? true,
      };

      if (editingDepartmentId) {
        const original = departments.find(department => department.id === editingDepartmentId);
        await updateDepartment(editingDepartmentId, {
          ...payload,
          clearParentDepartment: !payload.parentDepartmentId && Boolean(original?.parentDepartmentId) ? true : undefined,
        });
      } else {
        await createDepartment(payload);
      }

      await loadDepartmentsForCompany(selectedCompanyId);
      cancelDepartmentForm();
    } catch (error) {
      setDepartmentError(error instanceof Error ? error.message : t('failedToSaveDepartment', { ns: 'companies' }));
    } finally {
      setSavingDepartment(false);
    }
  };

  const removeDepartment = async (departmentId: string) => {
    if (!selectedCompanyId || !window.confirm(t('confirmDeleteDepartment', { ns: 'companies' }))) {
      return;
    }

    setSavingDepartment(true);
    setDepartmentError(null);

    try {
      await deleteDepartment(departmentId);
      if (editingDepartmentId === departmentId) {
        cancelDepartmentForm();
      }
      await loadDepartmentsForCompany(selectedCompanyId);
    } catch (error) {
      setDepartmentError(error instanceof Error ? error.message : t('failedToDeleteDepartment', { ns: 'companies' }));
    } finally {
      setSavingDepartment(false);
    }
  };

  const savePosition = async () => {
    if (!selectedCompanyId) {
      setPositionError(t('selectCompanyFirst', { ns: 'companies' }));
      return;
    }

    const name = (positionForm.name || '').trim();
    if (!name) {
      setPositionError(t('positionNameRequired', { ns: 'companies' }));
      return;
    }

    if (name.length > 100) {
      setPositionError(t('positionNameMaxLength', { ns: 'companies' }));
      return;
    }

    setSavingPosition(true);
    setPositionError(null);

    try {
      const payload: CreatePositionRequest = {
        name,
        departmentId: (positionForm.departmentId || '').trim() || undefined,
      };

      if (editingPositionId) {
        const original = positions.find(position => position.id === editingPositionId);
        await updatePosition(editingPositionId, {
          ...payload,
          clearDepartment: !payload.departmentId && Boolean(original?.departmentId) ? true : undefined,
        });
      } else {
        await createPosition(payload);
      }

      await loadPositionsForCompany(selectedCompanyId);
      cancelPositionForm();
    } catch (error) {
      setPositionError(error instanceof Error ? error.message : t('failedToSavePosition', { ns: 'companies' }));
    } finally {
      setSavingPosition(false);
    }
  };

  const removePosition = async (positionId: string) => {
    if (!selectedCompanyId || !window.confirm(t('confirmDeletePosition', { ns: 'companies' }))) {
      return;
    }

    setSavingPosition(true);
    setPositionError(null);

    try {
      await deletePosition(positionId);
      if (editingPositionId === positionId) {
        cancelPositionForm();
      }
      await loadPositionsForCompany(selectedCompanyId);
    } catch (error) {
      setPositionError(error instanceof Error ? error.message : t('failedToDeletePosition', { ns: 'companies' }));
    } finally {
      setSavingPosition(false);
    }
  };

  const startEditLocation = (location: Location) => {
    setEditingLocationId(location.id);
    setLocationForm(toLocationForm(location));
    setLocationError(null);
    setShowLocationForm(true);
  };

  const cancelLocationForm = () => {
    setEditingLocationId('');
    setLocationForm(emptyLocationForm);
    setLocationError(null);
    setShowLocationForm(false);
  };

  const saveLocation = async () => {
    if (!selectedCompanyId) {
      setLocationError(t('selectCompanyFirst', { ns: 'companies' }));
      return;
    }

    const name = (locationForm.name || '').trim();
    if (!name) {
      setLocationError(t('locationNameRequired', { ns: 'companies' }));
      return;
    }

    setSavingLocation(true);
    setLocationError(null);

    try {
      const payload: CreateLocationRequest = {
        ...locationForm,
        name,
        code: (locationForm.code || '').trim() || undefined,
        email: (locationForm.email || '').trim() || undefined,
        phone: (locationForm.phone || '').trim() || undefined,
        city: (locationForm.city || '').trim() || undefined,
        country: (locationForm.country || '').trim() || undefined,
        latitude: finiteNumber(locationForm.latitude),
        longitude: finiteNumber(locationForm.longitude),
        checkInRadiusMeters: finiteNumber(locationForm.checkInRadiusMeters),
      };

      if (editingLocationId) {
        await updateLocation(editingLocationId, payload);
      } else {
        await createLocation(payload);
      }

      await loadLocationsForCompany(selectedCompanyId);
      setEditingLocationId('');
      setLocationForm(emptyLocationForm);
      setShowLocationForm(false);
    } catch (error) {
      setLocationError(error instanceof Error ? error.message : t('failedToSaveLocation', { ns: 'companies' }));
    } finally {
      setSavingLocation(false);
    }
  };

  const removeLocation = async (locationId: string) => {
    if (!selectedCompanyId || !window.confirm(t('confirmDeleteLocation', { ns: 'companies' }))) {
      return;
    }

    setSavingLocation(true);
    setLocationError(null);

    try {
      await deleteLocation(locationId);
      if (editingLocationId === locationId) {
        cancelLocationForm();
      }
      await loadLocationsForCompany(selectedCompanyId);
    } catch (error) {
      setLocationError(error instanceof Error ? error.message : t('failedToDeleteLocation', { ns: 'companies' }));
    } finally {
      setSavingLocation(false);
    }
  };

  const startCreateRoomGroup = () => {
    setEditingRoomGroupId('');
    setRoomGroupForm(emptyRoomGroupForm);
    setRoomGroupError(null);
    setShowRoomGroupForm(true);
  };

  const startEditRoomGroup = (roomGroup: RoomGroup) => {
    setEditingRoomGroupId(roomGroup.id);
    setRoomGroupForm(toRoomGroupForm(roomGroup));
    setRoomGroupError(null);
    setShowRoomGroupForm(true);
  };

  const cancelRoomGroupForm = () => {
    setEditingRoomGroupId('');
    setRoomGroupForm(emptyRoomGroupForm);
    setRoomGroupError(null);
    setShowRoomGroupForm(false);
  };

  const saveRoomGroup = async () => {
    if (!selectedCompanyId || !selectedLocationIdForRooms) {
      setRoomGroupError(t('selectCompanyAndLocationFirst', { ns: 'companies' }));
      return;
    }

    const name = (roomGroupForm.name || '').trim();
    if (!name) {
      setRoomGroupError(t('roomGroupNameRequired', { ns: 'companies' }));
      return;
    }

    if (name.length > 200) {
      setRoomGroupError(t('roomGroupNameMaxLength', { ns: 'companies' }));
      return;
    }

    setSavingRoomGroup(true);
    setRoomGroupError(null);

    try {
      const payload: CreateRoomGroupRequest = {
        ...roomGroupForm,
        name,
        description: (roomGroupForm.description || '').trim() || undefined,
      };

      if (editingRoomGroupId) {
        await updateRoomGroup(selectedLocationIdForRooms, editingRoomGroupId, payload);
      } else {
        await createRoomGroup(selectedLocationIdForRooms, payload);
      }

      await loadRoomGroupsForLocation(selectedCompanyId, selectedLocationIdForRooms);
      setEditingRoomGroupId('');
      setRoomGroupForm(emptyRoomGroupForm);
      setShowRoomGroupForm(false);
    } catch (error) {
      setRoomGroupError(error instanceof Error ? error.message : t('failedToSaveRoomGroup', { ns: 'companies' }));
    } finally {
      setSavingRoomGroup(false);
    }
  };

  const removeRoomGroup = async (roomGroupId: string) => {
    if (!selectedCompanyId || !selectedLocationIdForRooms || !window.confirm(t('confirmDeleteRoomGroup', { ns: 'companies' }))) {
      return;
    }

    setSavingRoomGroup(true);
    setRoomGroupError(null);

    try {
      await deleteRoomGroup(selectedLocationIdForRooms, roomGroupId);
      if (editingRoomGroupId === roomGroupId) {
        cancelRoomGroupForm();
      }
      await loadRoomGroupsForLocation(selectedCompanyId, selectedLocationIdForRooms);
    } catch (error) {
      setRoomGroupError(error instanceof Error ? error.message : t('failedToDeleteRoomGroup', { ns: 'companies' }));
    } finally {
      setSavingRoomGroup(false);
    }
  };

  const startCreateRoom = () => {
    setEditingRoomId('');
    setRoomForm(emptyRoomForm);
    setRoomFormRoomGroupId(selectedRoomGroupId || roomGroups[0]?.id || '');
    setRoomError(null);
    setShowRoomForm(true);
  };

  const startEditRoom = (room: Room) => {
    setEditingRoomId(room.id);
    setRoomForm(toRoomForm(room));
    setRoomFormRoomGroupId(room.roomGroupId || selectedRoomGroupId || '');
    setRoomError(null);
    setShowRoomForm(true);
  };

  const cancelRoomForm = () => {
    setEditingRoomId('');
    setRoomForm(emptyRoomForm);
    setRoomFormRoomGroupId(selectedRoomGroupId || roomGroups[0]?.id || '');
    setRoomError(null);
    setShowRoomForm(false);
  };

  const saveRoom = async () => {
    if (!selectedCompanyId || !selectedLocationIdForRooms) {
      setRoomError(t('selectCompanyAndLocationFirst', { ns: 'companies' }));
      return;
    }

    const targetRoomGroupId = roomFormRoomGroupId || selectedRoomGroupId;
    if (!targetRoomGroupId) {
      setRoomError(t('selectRoomGroupForRoom', { ns: 'companies' }));
      return;
    }

    const name = (roomForm.name || '').trim();
    if (!name) {
      setRoomError(t('roomNameRequired', { ns: 'companies' }));
      return;
    }

    if (name.length > 200) {
      setRoomError(t('roomNameMaxLength', { ns: 'companies' }));
      return;
    }

    setSavingRoom(true);
    setRoomError(null);

    try {
      const capacityAsNumber = typeof roomForm.capacity === 'number'
        ? roomForm.capacity
        : Number.isFinite(Number(roomForm.capacity))
          ? Number(roomForm.capacity)
          : undefined;

      const payload: CreateRoomRequest = {
        ...roomForm,
        name,
        code: (roomForm.code || '').trim() || undefined,
        description: (roomForm.description || '').trim() || undefined,
        capacity: capacityAsNumber,
      };

      if (payload.code && payload.code.length > 60) {
        setRoomError(t('roomCodeMaxLength', { ns: 'companies' }));
        return;
      }

      if (payload.description && payload.description.length > 600) {
        setRoomError(t('roomDescriptionMaxLength', { ns: 'companies' }));
        return;
      }

      if (typeof payload.capacity === 'number' && payload.capacity < 0) {
        setRoomError(t('roomCapacityMin', { ns: 'companies' }));
        return;
      }

      if (editingRoomId) {
        const updatePayload: UpdateRoomRequest = {
          ...payload,
          roomGroupId: targetRoomGroupId,
        };
        await updateRoom(selectedLocationIdForRooms, selectedRoomGroupId, editingRoomId, updatePayload);
      } else {
        await createRoom(selectedLocationIdForRooms, targetRoomGroupId, payload);
      }

      if (targetRoomGroupId !== selectedRoomGroupId) {
        setSelectedRoomGroupId(targetRoomGroupId);
      }
      await loadRoomsForRoomGroup(selectedCompanyId, selectedLocationIdForRooms, targetRoomGroupId);
      setEditingRoomId('');
      setRoomForm(emptyRoomForm);
      setRoomFormRoomGroupId(targetRoomGroupId);
      setShowRoomForm(false);
    } catch (error) {
      setRoomError(error instanceof Error ? error.message : t('failedToSaveRoom', { ns: 'companies' }));
    } finally {
      setSavingRoom(false);
    }
  };

  const removeRoom = async (roomId: string) => {
    if (!selectedCompanyId || !selectedLocationIdForRooms || !selectedRoomGroupId || !window.confirm(t('confirmDeleteRoom', { ns: 'companies' }))) {
      return;
    }

    setSavingRoom(true);
    setRoomError(null);

    try {
      await deleteRoom(selectedLocationIdForRooms, selectedRoomGroupId, roomId);
      if (editingRoomId === roomId) {
        cancelRoomForm();
      }
      await loadRoomsForRoomGroup(selectedCompanyId, selectedLocationIdForRooms, selectedRoomGroupId);
    } catch (error) {
      setRoomError(error instanceof Error ? error.message : t('failedToDeleteRoom', { ns: 'companies' }));
    } finally {
      setSavingRoom(false);
    }
  };

  const startCreateOpeningHour = () => {
    setEditingOpeningHourId('');
    setOpeningHourForm(emptyOpeningHourForm);
    setOpeningHourError(null);
    setShowOpeningHourForm(true);
  };

  const startEditOpeningHour = (openingHour: CompanyOpeningHour) => {
    setEditingOpeningHourId(openingHour.id);
    setOpeningHourForm(toOpeningHourForm(openingHour));
    setOpeningHourError(null);
    setShowOpeningHourForm(true);
  };

  const cancelOpeningHourForm = () => {
    setEditingOpeningHourId('');
    setOpeningHourForm(emptyOpeningHourForm);
    setOpeningHourError(null);
    setShowOpeningHourForm(false);
  };

  const saveOpeningHour = async () => {
    if (!selectedCompanyId) {
      setOpeningHourError(t('selectCompanyFirst', { ns: 'companies' }));
      return;
    }

    if (!openingHourForm.daysOfWeek || openingHourForm.daysOfWeek.length === 0) {
      setOpeningHourError(t('selectAtLeastOneDayOfWeek', { ns: 'companies' }));
      return;
    }

    if (!openingHourForm.startTime || !openingHourForm.endTime) {
      setOpeningHourError(t('startAndEndTimeRequired', { ns: 'companies' }));
      return;
    }

    setSavingOpeningHour(true);
    setOpeningHourError(null);

    try {
      const payload: CreateCompanyOpeningHourRequest = {
        locationId: openingHourForm.locationId || undefined,
        repeatPattern: openingHourForm.repeatPattern || 'weekly',
        isAllDay: Boolean(openingHourForm.isAllDay),
        daysOfWeek: openingHourForm.daysOfWeek,
        startDate: openingHourForm.startDate || undefined,
        endDate: openingHourForm.endDate || undefined,
        startTime: openingHourForm.startTime,
        endTime: openingHourForm.endTime,
        isEnabled: openingHourForm.isEnabled ?? true,
      };

      if (editingOpeningHourId) {
        await updateOpeningHour(editingOpeningHourId, payload);
      } else {
        await createOpeningHour(payload);
      }

      await loadOpeningHoursForCompany(selectedCompanyId);
      cancelOpeningHourForm();
    } catch (error) {
      setOpeningHourError(error instanceof Error ? error.message : t('failedToSaveOpeningHour', { ns: 'companies' }));
    } finally {
      setSavingOpeningHour(false);
    }
  };

  const removeOpeningHour = async (openingHourId: string) => {
    if (!selectedCompanyId || !window.confirm(t('confirmDeleteOpeningHour', { ns: 'companies' }))) {
      return;
    }

    setSavingOpeningHour(true);
    setOpeningHourError(null);

    try {
      await deleteOpeningHour(openingHourId);
      if (editingOpeningHourId === openingHourId) {
        cancelOpeningHourForm();
      }
      await loadOpeningHoursForCompany(selectedCompanyId);
    } catch (error) {
      setOpeningHourError(error instanceof Error ? error.message : t('failedToDeleteOpeningHour', { ns: 'companies' }));
    } finally {
      setSavingOpeningHour(false);
    }
  };

  const startCreateClosure = () => {
    setEditingClosureId('');
    setClosureForm(emptyClosureForm);
    setClosureError(null);
    setShowClosureForm(true);
  };

  const startEditClosure = (closure: CompanyClosure) => {
    setEditingClosureId(closure.id);
    setClosureForm(toClosureForm(closure));
    setClosureError(null);
    setShowClosureForm(true);
  };

  const cancelClosureForm = () => {
    setEditingClosureId('');
    setClosureForm(emptyClosureForm);
    setClosureError(null);
    setShowClosureForm(false);
  };

  const saveClosure = async () => {
    if (!selectedCompanyId) {
      setClosureError(t('selectCompanyFirst', { ns: 'companies' }));
      return;
    }

    if (!closureForm.startAtUtc || !closureForm.endAtUtc) {
      setClosureError(t('startAndEndDateTimeRequired', { ns: 'companies' }));
      return;
    }

    setSavingClosure(true);
    setClosureError(null);

    try {
      const payload: CreateCompanyClosureRequest = {
        locationId: closureForm.locationId || undefined,
        repeatPattern: closureForm.repeatPattern || 'none',
        isAllDay: Boolean(closureForm.isAllDay),
        startAtUtc: closureForm.startAtUtc,
        endAtUtc: closureForm.endAtUtc,
        isEnabled: closureForm.isEnabled ?? true,
      };

      if (editingClosureId) {
        await updateClosure(editingClosureId, payload);
      } else {
        await createClosure(payload);
      }

      await loadClosuresForCompany(selectedCompanyId);
      cancelClosureForm();
    } catch (error) {
      setClosureError(error instanceof Error ? error.message : t('failedToSaveClosure', { ns: 'companies' }));
    } finally {
      setSavingClosure(false);
    }
  };

  const removeClosure = async (closureId: string) => {
    if (!selectedCompanyId || !window.confirm(t('confirmDeleteClosure', { ns: 'companies' }))) {
      return;
    }

    setSavingClosure(true);
    setClosureError(null);

    try {
      await deleteClosure(closureId);
      if (editingClosureId === closureId) {
        cancelClosureForm();
      }
      await loadClosuresForCompany(selectedCompanyId);
    } catch (error) {
      setClosureError(error instanceof Error ? error.message : t('failedToDeleteClosure', { ns: 'companies' }));
    } finally {
      setSavingClosure(false);
    }
  };

  return (
    <div className="company-admin-page">
      <Breadcrumb items={breadcrumbItems} />
      <div className="company-page-head">
        <div>
          <h2>{t('companyPageTitle', { ns: 'companies' })}</h2>
          <p className="company-page-head-description">
            {t('companyPageSubtitle', { ns: 'companies' })}
          </p>
        </div>
        <div className="company-tabs" role="tablist" aria-label={t('companyManagementTabs', { ns: 'companies' })}>
          {visibleCompanyPageTabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`company-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleActiveTabChange(tab.id)}
            >
              {t(tab.label, { ns: 'companies' })}
            </button>
          ))}
        </div>
      </div>

      <div className="company-tab-panel">
      {visibleCompanyPageTabs.length === 0 ? (
        <div className="company-tab-readonly-hint" role="status">
          {t('companyNoTabAccessHint', { ns: 'companies' })}
        </div>
      ) : null}
      {activeTab === 'company' ? (
        <>
          {!canManageCompany ? <CompanyTabReadOnlyBanner /> : null}
          <section className="company-section">
            <div className="company-section-head">
              <h3>{t('companyDirectory', { ns: 'companies' })}</h3>
              {canCreateCompany ? (
                <button type="button" className="btn btn-secondary" onClick={startCreateCompany}>{t('createCompany', { ns: 'companies' })}</button>
              ) : null}
            </div>

        {companyError ? <div className="error">{companyError}</div> : null}

        {loadingCompanies ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
            <Spinner text={t('loadingCompanies', { ns: 'companies' })} />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('name', { ns: 'admin' })}</th>
                <th>{t('email', { ns: 'admin' })}</th>
                <th>{t('city', { ns: 'contacts' })}</th>
                <th>{t('status', { ns: 'admin' })}</th>
                <th>{t('actions', { ns: 'admin' })}</th>
              </tr>
            </thead>
            <tbody>
              {companies.map(company => (
                <tr
                  key={company.id}
                  style={{ background: company.id === selectedCompanyId ? '#f5f8ff' : undefined }}
                >
                  <td>{company.name}</td>
                  <td>{company.email || '-'}</td>
                  <td>{company.city || '-'}</td>
                  <td>
                    <span className={`company-status-pill ${company.isActive ? '' : 'is-inactive'}`}>
                      {company.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
                    </span>
                  </td>
                  <td>
                    <div className="company-actions-inline" onClick={event => event.stopPropagation()}>
                      {canManageCompany ? (
                        <button type="button" className="btn btn-secondary" onClick={() => startEditCompany(company)}>{t('edit', { ns: 'common' })}</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {companies.length === 0 ? (
                <tr>
                  <td colSpan={5}>{t('noCompaniesFound', { ns: 'companies' })}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
          </section>

          {showCompanyForm && canManageCompany ? (
            <section className="company-section">
              <div className="company-section-head">
                <h3>{editingCompanyId ? t('editCompany', { ns: 'companies' }) : t('createCompany', { ns: 'companies' })}</h3>
              </div>

        <div className="company-form-grid">
          <label>
            {t('companyName', { ns: 'companies' })}
            <input
              className="text-input"
              value={companyForm.name || ''}
              onChange={event => setCompanyForm(prev => ({ ...prev, name: event.target.value }))}
            />
          </label>
          <label>
            {t('mailerFrom', { ns: 'companies' })}
            <input
              className="text-input"
              value={companyForm.mailerFrom || ''}
              onChange={event => setCompanyForm(prev => ({ ...prev, mailerFrom: event.target.value }))}
            />
          </label>
          <label>
            {t('email', { ns: 'admin' })}
            <input
              className="text-input"
              value={companyForm.email || ''}
              onChange={event => setCompanyForm(prev => ({ ...prev, email: event.target.value }))}
            />
          </label>
          <label>
            {t('phone', { ns: 'contacts' })}
            <input
              className="text-input"
              value={companyForm.phone || ''}
              onChange={event => setCompanyForm(prev => ({ ...prev, phone: event.target.value }))}
            />
          </label>
          <label>
            {t('city', { ns: 'contacts' })}
            <input
              className="text-input"
              value={companyForm.city || ''}
              onChange={event => setCompanyForm(prev => ({ ...prev, city: event.target.value }))}
            />
          </label>
          <label>
            {t('country', { ns: 'contacts' })}
            <input
              className="text-input"
              value={companyForm.country || ''}
              onChange={event => setCompanyForm(prev => ({ ...prev, country: event.target.value }))}
            />
          </label>
          <label>
            {t('language', { ns: 'admin' })}
            <select
              className="text-input"
              value={companyForm.language || ''}
              onChange={event => setCompanyForm(prev => ({ ...prev, language: event.target.value }))}
            >
              <option value="">{t('selectLanguage', { ns: 'admin' })}</option>
              <option value="az">AZ</option>
              <option value="en">EN</option>
              <option value="tr">TR</option>
            </select>
          </label>
          <label>
            {t('status', { ns: 'admin' })}
            <select
              className="text-input"
              value={companyForm.isActive ? 'active' : 'inactive'}
              onChange={event => setCompanyForm(prev => ({ ...prev, isActive: event.target.value === 'active' }))}
            >
              <option value="active">{t('active', { ns: 'admin' })}</option>
              <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
            </select>
          </label>
        </div>

          <div className="company-actions-inline">
            <button type="button" className="btn btn-primary" onClick={saveCompany} disabled={savingCompany}>
              {savingCompany ? t('savingEllipsis', { ns: 'admin' }) : editingCompanyId ? t('updateCompany', { ns: 'companies' }) : t('createCompany', { ns: 'companies' })}
            </button>
            <button type="button" className="btn btn-secondary" onClick={cancelEditCompany} disabled={savingCompany}>
              {editingCompanyId ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
            </button>
          </div>
            </section>
          ) : null}

        </>
      ) : null}

      {activeTab === 'departments' ? (
        <section className="company-section">
          {!canManageDepartments ? <CompanyTabReadOnlyBanner /> : null}
          <div className="company-section-head">
            <h3>{selectedCompany ? t('departmentsFor', { ns: 'companies', name: selectedCompany.name }) : t('departments', { ns: 'companies' })}</h3>
            {canManageDepartments ? (
              <button type="button" className="btn btn-secondary" onClick={startCreateDepartment} disabled={!selectedCompanyId}>{t('newDepartment', { ns: 'companies' })}</button>
            ) : null}
          </div>

        {departmentError && !showDepartmentForm ? <div className="error">{departmentError}</div> : null}

        {!selectedCompanyId ? (
          <div>{t('selectCompanyToManageDepartments', { ns: 'companies' })}</div>
        ) : loadingDepartments ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
            <Spinner text={t('loadingDepartments', { ns: 'companies' })} />
          </div>
        ) : (
          <DepartmentTreeList
            departments={departments}
            saving={savingDepartment}
            canManage={canManageDepartments}
            onEdit={startEditDepartment}
            onDelete={departmentId => void removeDepartment(departmentId)}
          />
        )}

        </section>
      ) : null}

      {activeTab === 'positions' ? (
        <section className="company-section">
          {!canManagePositions ? <CompanyTabReadOnlyBanner /> : null}
          <div className="company-section-head">
            <h3>{selectedCompany ? t('positionsFor', { ns: 'companies', name: selectedCompany.name }) : t('positions', { ns: 'companies' })}</h3>
            {canManagePositions ? (
              <button type="button" className="btn btn-secondary" onClick={startCreatePosition} disabled={!selectedCompanyId}>{t('newPosition', { ns: 'companies' })}</button>
            ) : null}
          </div>

          {positionError ? <div className="error">{positionError}</div> : null}

          {!selectedCompanyId ? (
            <div>{t('selectCompanyToManagePositions', { ns: 'companies' })}</div>
          ) : loadingPositions ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
              <Spinner text={t('loadingPositions', { ns: 'companies' })} />
            </div>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>{t('name', { ns: 'admin' })}</th>
                    <th>{t('department', { ns: 'admin' })}</th>
                    <th>{t('actions', { ns: 'admin' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map(position => (
                    <tr key={position.id}>
                      <td>{position.name}</td>
                      <td>{position.departmentId ? departmentLabelById.get(position.departmentId) || position.departmentName || '-' : t('allDepartments', { ns: 'companies' })}</td>
                      <td>
                        <div className="company-actions-inline">
                          {canManagePositions ? (
                            <>
                              <button type="button" className="btn btn-secondary" onClick={() => startEditPosition(position)}>{t('edit', { ns: 'common' })}</button>
                              <button type="button" className="btn btn-secondary btn-danger-subtle" onClick={() => removePosition(position.id)} disabled={savingPosition}>{t('delete', { ns: 'common' })}</button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {positions.length === 0 ? (
                    <tr>
                      <td colSpan={3}>{t('noPositionsFound', { ns: 'companies' })}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </>
          )}
        </section>
      ) : null}

      {activeTab === 'locations' ? (
        <section className="company-section">
          {!canManageLocations ? <CompanyTabReadOnlyBanner /> : null}
          <div className="company-section-head">
            <h3>{selectedCompany ? t('locationsFor', { ns: 'companies', name: selectedCompany.name }) : t('locations', { ns: 'companies' })}</h3>
            {canManageLocations ? (
              <button type="button" className="btn btn-secondary" onClick={startCreateLocation} disabled={!selectedCompanyId}>{t('newLocation', { ns: 'companies' })}</button>
            ) : null}
          </div>

        {locationError ? <div className="error">{locationError}</div> : null}

        {!selectedCompanyId ? (
          <div>{t('selectCompanyToManageLocations', { ns: 'companies' })}</div>
        ) : loadingLocations ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
            <Spinner text={t('loadingLocations', { ns: 'companies' })} />
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>{t('name', { ns: 'admin' })}</th>
                  <th>{t('code', { ns: 'companies' })}</th>
                  <th>{t('city', { ns: 'contacts' })}</th>
                  <th>{t('latitude', { ns: 'companies' })} / {t('longitude', { ns: 'companies' })}</th>
                  <th>{t('primary', { ns: 'companies' })}</th>
                  <th>{t('status', { ns: 'admin' })}</th>
                  <th>{t('actions', { ns: 'admin' })}</th>
                </tr>
              </thead>
              <tbody>
                {locations.map(location => (
                  <tr key={location.id}>
                    <td>{location.name}</td>
                    <td>{location.code || '-'}</td>
                    <td>{location.city || '-'}</td>
                    <td>{location.latitude != null && location.longitude != null ? `${location.latitude}, ${location.longitude}` : '-'}</td>
                    <td>{location.isPrimary ? t('yes', { ns: 'admin' }) : t('no', { ns: 'admin' })}</td>
                    <td>
                      <span className={`company-status-pill ${location.isActive ? '' : 'is-inactive'}`}>
                        {location.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
                      </span>
                    </td>
                    <td>
                      <div className="company-actions-inline">
                        {canManageLocations ? (
                          <>
                            <button type="button" className="btn btn-secondary" onClick={() => startEditLocation(location)}>{t('edit', { ns: 'common' })}</button>
                            <button type="button" className="btn btn-secondary btn-danger-subtle" onClick={() => removeLocation(location.id)} disabled={savingLocation}>{t('delete', { ns: 'common' })}</button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {locations.length === 0 ? (
                  <tr>
                    <td colSpan={7}>{t('noLocationsFound', { ns: 'companies' })}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            {showLocationForm && canManageLocations ? (
            <div className="company-section" style={{ marginTop: 16 }}>
              <div className="company-section-head">
                <h3>{editingLocationId ? t('editLocation', { ns: 'companies' }) : t('createLocation', { ns: 'companies' })}</h3>
              </div>

              <div className="company-form-grid">
                <label>
                  {t('locationName', { ns: 'companies' })}
                  <input
                    className="text-input"
                    value={locationForm.name || ''}
                    onChange={event => setLocationForm(prev => ({ ...prev, name: event.target.value }))}
                  />
                </label>
                <label>
                  {t('code', { ns: 'companies' })}
                  <input
                    className="text-input"
                    value={locationForm.code || ''}
                    onChange={event => setLocationForm(prev => ({ ...prev, code: event.target.value }))}
                  />
                </label>
                <label>
                  {t('email', { ns: 'admin' })}
                  <input
                    className="text-input"
                    value={locationForm.email || ''}
                    onChange={event => setLocationForm(prev => ({ ...prev, email: event.target.value }))}
                  />
                </label>
                <label>
                  {t('phone', { ns: 'contacts' })}
                  <input
                    className="text-input"
                    value={locationForm.phone || ''}
                    onChange={event => setLocationForm(prev => ({ ...prev, phone: event.target.value }))}
                  />
                </label>
                <label>
                  {t('city', { ns: 'contacts' })}
                  <input
                    className="text-input"
                    value={locationForm.city || ''}
                    onChange={event => setLocationForm(prev => ({ ...prev, city: event.target.value }))}
                  />
                </label>
                <label>
                  {t('country', { ns: 'contacts' })}
                  <input
                    className="text-input"
                    value={locationForm.country || ''}
                    onChange={event => setLocationForm(prev => ({ ...prev, country: event.target.value }))}
                  />
                </label>
                <label>
                  {t('latitude', { ns: 'companies' })}
                  <input
                    className="text-input"
                    type="number"
                    step="0.000001"
                    value={locationForm.latitude ?? ''}
                    onChange={event => setLocationForm(prev => ({
                      ...prev,
                      latitude: event.target.value === '' ? undefined : Number(event.target.value),
                    }))}
                  />
                </label>
                <label>
                  {t('longitude', { ns: 'companies' })}
                  <input
                    className="text-input"
                    type="number"
                    step="0.000001"
                    value={locationForm.longitude ?? ''}
                    onChange={event => setLocationForm(prev => ({
                      ...prev,
                      longitude: event.target.value === '' ? undefined : Number(event.target.value),
                    }))}
                  />
                </label>
                <label>
                  {t('checkInRadiusMeters', { ns: 'companies' })}
                  <input
                    className="text-input"
                    type="number"
                    min={10}
                    max={10000}
                    value={locationForm.checkInRadiusMeters ?? ''}
                    onChange={event => setLocationForm(prev => ({
                      ...prev,
                      checkInRadiusMeters: event.target.value === '' ? undefined : Number(event.target.value),
                    }))}
                  />
                </label>
                <div className="company-form-span">
                  <LocationMapPicker
                    key={editingLocationId || 'new'}
                    latitude={finiteNumber(locationForm.latitude)}
                    longitude={finiteNumber(locationForm.longitude)}
                    radiusMeters={finiteNumber(locationForm.checkInRadiusMeters)}
                    disabled={savingLocation}
                    onChange={(nextLatitude, nextLongitude) => setLocationForm(prev => ({
                      ...prev,
                      latitude: nextLatitude,
                      longitude: nextLongitude,
                      checkInRadiusMeters: prev.checkInRadiusMeters ?? 150,
                    }))}
                  />
                </div>
                <label>
                  {t('primary', { ns: 'companies' })}
                  <select
                    className="text-input"
                    value={locationForm.isPrimary ? 'yes' : 'no'}
                    onChange={event => setLocationForm(prev => ({ ...prev, isPrimary: event.target.value === 'yes' }))}
                  >
                    <option value="no">{t('no', { ns: 'admin' })}</option>
                    <option value="yes">{t('yes', { ns: 'admin' })}</option>
                  </select>
                </label>
                <label>
                  {t('status', { ns: 'admin' })}
                  <select
                    className="text-input"
                    value={locationForm.isActive ? 'active' : 'inactive'}
                    onChange={event => setLocationForm(prev => ({ ...prev, isActive: event.target.value === 'active' }))}
                  >
                    <option value="active">{t('active', { ns: 'admin' })}</option>
                    <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
                  </select>
                </label>
              </div>

              <div className="company-actions-inline">
                <button type="button" className="btn btn-primary" onClick={saveLocation} disabled={savingLocation}>
                  {savingLocation ? t('savingEllipsis', { ns: 'admin' }) : editingLocationId ? t('updateLocation', { ns: 'companies' }) : t('createLocation', { ns: 'companies' })}
                </button>
                <button type="button" className="btn btn-secondary" onClick={cancelLocationForm} disabled={savingLocation}>
                  {editingLocationId ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
                </button>
              </div>
            </div>
            ) : null}
          </>
        )}
        </section>
      ) : null}

      {activeTab === 'rooms' ? (
        <>
          {!canManageRooms ? <CompanyTabReadOnlyBanner /> : null}
          <section className="company-section">
            <div className="company-section-head">
              <h3>{t('roomGroups', { ns: 'companies' })}</h3>
              {canManageRooms ? (
                <button type="button" className="btn btn-secondary" onClick={startCreateRoomGroup} disabled={!selectedCompanyId || !selectedLocationIdForRooms}>{t('newRoomGroup', { ns: 'companies' })}</button>
              ) : null}
            </div>

            <div className="company-toolbar">
              <select
                className="text-input"
                value={selectedLocationIdForRooms}
                onChange={event => setSelectedLocationIdForRooms(event.target.value)}
                disabled={!selectedCompanyId || locations.length === 0}
              >
                {locations.length === 0 ? <option value="">{t('noLocationsAvailable', { ns: 'companies' })}</option> : null}
                {locations.map(location => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </div>

            {roomGroupError ? <div className="error">{roomGroupError}</div> : null}

            {!selectedCompanyId ? (
              <div>{t('selectCompanyFirst', { ns: 'companies' })}</div>
            ) : !selectedLocationIdForRooms ? (
              <div>{t('selectLocationToManageRoomGroups', { ns: 'companies' })}</div>
            ) : loadingRoomGroups ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <Spinner text={t('loadingRoomGroups', { ns: 'companies' })} />
              </div>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>{t('name', { ns: 'admin' })}</th>
                      <th>{t('description', { ns: 'admin' })}</th>
                      <th>{t('status', { ns: 'admin' })}</th>
                      <th>{t('actions', { ns: 'admin' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roomGroups.map(roomGroup => (
                      <tr
                        key={roomGroup.id}
                        onClick={() => setSelectedRoomGroupId(roomGroup.id)}
                        style={{ background: roomGroup.id === selectedRoomGroupId ? '#f5f8ff' : undefined, cursor: 'pointer' }}
                      >
                        <td>{roomGroup.name}</td>
                        <td>{roomGroup.description || '-'}</td>
                        <td>
                          <span className={`company-status-pill ${roomGroup.isActive ? '' : 'is-inactive'}`}>
                            {roomGroup.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
                          </span>
                        </td>
                        <td>
                          <div className="company-actions-inline" onClick={event => event.stopPropagation()}>
                            {canManageRooms ? (
                              <>
                                <button type="button" className="btn btn-secondary" onClick={() => startEditRoomGroup(roomGroup)}>{t('edit', { ns: 'common' })}</button>
                                <button type="button" className="btn btn-secondary btn-danger-subtle" onClick={() => removeRoomGroup(roomGroup.id)} disabled={savingRoomGroup}>{t('delete', { ns: 'common' })}</button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {roomGroups.length === 0 ? (
                      <tr>
                        <td colSpan={4}>{t('noRoomGroupsFound', { ns: 'companies' })}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>

                {showRoomGroupForm && canManageRooms ? (
                <div className="company-section" style={{ marginTop: 16 }}>
                  <div className="company-section-head">
                    <h3>{editingRoomGroupId ? t('editRoomGroup', { ns: 'companies' }) : t('createRoomGroup', { ns: 'companies' })}</h3>
                  </div>
                  <div className="company-form-grid">
                    <label>
                      {t('name', { ns: 'admin' })}
                      <input
                        className="text-input"
                        value={roomGroupForm.name || ''}
                        onChange={event => setRoomGroupForm(prev => ({ ...prev, name: event.target.value }))}
                      />
                    </label>
                    <label>
                      {t('description', { ns: 'admin' })}
                      <input
                        className="text-input"
                        value={roomGroupForm.description || ''}
                        onChange={event => setRoomGroupForm(prev => ({ ...prev, description: event.target.value }))}
                      />
                    </label>
                    <label>
                      {t('status', { ns: 'admin' })}
                      <select
                        className="text-input"
                        value={roomGroupForm.isActive ? 'active' : 'inactive'}
                        onChange={event => setRoomGroupForm(prev => ({ ...prev, isActive: event.target.value === 'active' }))}
                      >
                        <option value="active">{t('active', { ns: 'admin' })}</option>
                        <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
                      </select>
                    </label>
                  </div>
                  <div className="company-actions-inline">
                    <button type="button" className="btn btn-primary" onClick={saveRoomGroup} disabled={savingRoomGroup}>
                      {savingRoomGroup ? t('savingEllipsis', { ns: 'admin' }) : editingRoomGroupId ? t('updateRoomGroup', { ns: 'companies' }) : t('createRoomGroup', { ns: 'companies' })}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={cancelRoomGroupForm} disabled={savingRoomGroup}>
                      {editingRoomGroupId ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
                    </button>
                  </div>
                </div>
                ) : null}
              </>
            )}
          </section>

          <section className="company-section">
            <div className="company-section-head">
              <h3>{selectedRoomGroup ? t('roomsIn', { ns: 'companies', name: selectedRoomGroup.name }) : t('rooms', { ns: 'companies' })}</h3>
              {canManageRooms ? (
                <button type="button" className="btn btn-secondary" onClick={startCreateRoom} disabled={!selectedLocationIdForRooms || roomGroups.length === 0}>{t('newRoom', { ns: 'companies' })}</button>
              ) : null}
            </div>

            {roomError ? <div className="error">{roomError}</div> : null}

            {!selectedRoomGroupId ? (
              <div>{t('selectRoomGroupToManageRooms', { ns: 'companies' })}</div>
            ) : loadingRooms ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <Spinner text={t('loadingRooms', { ns: 'companies' })} />
              </div>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>{t('name', { ns: 'admin' })}</th>
                      <th>{t('code', { ns: 'companies' })}</th>
                      <th>{t('capacity', { ns: 'companies' })}</th>
                      <th>{t('status', { ns: 'admin' })}</th>
                      <th>{t('actions', { ns: 'admin' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map(room => (
                      <tr key={room.id}>
                        <td>{room.name}</td>
                        <td>{room.code || '-'}</td>
                        <td>{typeof room.capacity === 'number' ? room.capacity : '-'}</td>
                        <td>
                          <span className={`company-status-pill ${room.isActive ? '' : 'is-inactive'}`}>
                            {room.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
                          </span>
                        </td>
                        <td>
                          <div className="company-actions-inline">
                            {canManageRooms ? (
                              <>
                                <button type="button" className="btn btn-secondary" onClick={() => startEditRoom(room)}>{t('edit', { ns: 'common' })}</button>
                                <button type="button" className="btn btn-secondary btn-danger-subtle" onClick={() => removeRoom(room.id)} disabled={savingRoom}>{t('delete', { ns: 'common' })}</button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rooms.length === 0 ? (
                      <tr>
                        <td colSpan={5}>{t('noRoomsFound', { ns: 'companies' })}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>

                {showRoomForm && canManageRooms ? (
                <div className="company-section" style={{ marginTop: 16 }}>
                  <div className="company-section-head">
                    <h3>{editingRoomId ? t('editRoom', { ns: 'companies' }) : t('createRoom', { ns: 'companies' })}</h3>
                  </div>
                  <div className="company-form-grid">
                    <label>
                      {t('roomGroup', { ns: 'companies' })}
                      <select
                        className="text-input"
                        value={roomFormRoomGroupId}
                        onChange={event => setRoomFormRoomGroupId(event.target.value)}
                      >
                        <option value="">{t('selectRoomGroup', { ns: 'companies' })}</option>
                        {roomGroups.map(roomGroup => (
                          <option key={roomGroup.id} value={roomGroup.id}>
                            {roomGroup.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t('roomName', { ns: 'companies' })}
                      <input
                        className="text-input"
                        value={roomForm.name || ''}
                        onChange={event => setRoomForm(prev => ({ ...prev, name: event.target.value }))}
                      />
                    </label>
                    <label>
                      {t('code', { ns: 'companies' })}
                      <input
                        className="text-input"
                        value={roomForm.code || ''}
                        onChange={event => setRoomForm(prev => ({ ...prev, code: event.target.value }))}
                      />
                    </label>
                    <label>
                      {t('description', { ns: 'admin' })}
                      <input
                        className="text-input"
                        value={roomForm.description || ''}
                        onChange={event => setRoomForm(prev => ({ ...prev, description: event.target.value }))}
                      />
                    </label>
                    <label>
                      {t('capacity', { ns: 'companies' })}
                      <input
                        className="text-input"
                        type="number"
                        min={0}
                        value={typeof roomForm.capacity === 'number' ? String(roomForm.capacity) : ''}
                        onChange={event => {
                          const value = event.target.value;
                          setRoomForm(prev => ({ ...prev, capacity: value === '' ? undefined : Number(value) }));
                        }}
                      />
                    </label>
                    <label>
                      {t('status', { ns: 'admin' })}
                      <select
                        className="text-input"
                        value={roomForm.isActive ? 'active' : 'inactive'}
                        onChange={event => setRoomForm(prev => ({ ...prev, isActive: event.target.value === 'active' }))}
                      >
                        <option value="active">{t('active', { ns: 'admin' })}</option>
                        <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
                      </select>
                    </label>
                  </div>
                  <div className="company-actions-inline">
                    <button type="button" className="btn btn-primary" onClick={saveRoom} disabled={savingRoom}>
                      {savingRoom ? t('savingEllipsis', { ns: 'admin' }) : editingRoomId ? t('updateRoom', { ns: 'companies' }) : t('createRoom', { ns: 'companies' })}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={cancelRoomForm} disabled={savingRoom}>
                      {editingRoomId ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
                    </button>
                  </div>
                </div>
                ) : null}
              </>
            )}
          </section>
        </>
      ) : null}

      {activeTab === 'business-hours' ? (
        <>
          {!canManageBusinessHours ? <CompanyTabReadOnlyBanner /> : null}
          <section className="company-section">
            <div className="company-section-head">
              <h3>{t('openingHours', { ns: 'companies' })}</h3>
              {canManageBusinessHours ? (
                <button type="button" className="btn btn-secondary" onClick={startCreateOpeningHour} disabled={!selectedCompanyId}>{t('newOpeningHour', { ns: 'companies' })}</button>
              ) : null}
            </div>

            {openingHourError ? <div className="error">{openingHourError}</div> : null}

            {!selectedCompanyId ? (
              <div>{t('selectCompanyFirst', { ns: 'companies' })}</div>
            ) : loadingOpeningHours ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <Spinner text={t('loadingOpeningHours', { ns: 'companies' })} />
              </div>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>{t('location', { ns: 'companies' })}</th>
                      <th>{t('repeat', { ns: 'companies' })}</th>
                      <th>{t('days', { ns: 'companies' })}</th>
                      <th>{t('time', { ns: 'companies' })}</th>
                      <th>{t('status', { ns: 'admin' })}</th>
                      <th>{t('actions', { ns: 'admin' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openingHours.map(openingHour => (
                      <tr key={openingHour.id}>
                        <td>{locations.find(location => location.id === openingHour.locationId)?.name || t('allLocations', { ns: 'companies' })}</td>
                        <td>{repeatPatternOptions.find(option => option.value === openingHour.repeatPattern)?.label || openingHour.repeatPattern}</td>
                        <td>{openingHour.daysOfWeek.map(day => dayOptions.find(item => item.value === day)?.label || String(day)).join(', ') || '-'}</td>
                        <td>{openingHour.isAllDay ? t('allDay', { ns: 'companies' }) : formatBusinessTimeRangeLabel(openingHour.startTime, openingHour.endTime)}</td>
                        <td>
                          <span className={`company-status-pill ${openingHour.isEnabled ? '' : 'is-inactive'}`}>
                            {openingHour.isEnabled ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
                          </span>
                        </td>
                        <td>
                          <div className="company-actions-inline">
                            {canManageBusinessHours ? (
                              <>
                                <button type="button" className="btn btn-secondary" onClick={() => startEditOpeningHour(openingHour)}>{t('edit', { ns: 'common' })}</button>
                                <button type="button" className="btn btn-secondary btn-danger-subtle" onClick={() => removeOpeningHour(openingHour.id)} disabled={savingOpeningHour}>{t('delete', { ns: 'common' })}</button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {openingHours.length === 0 ? (
                      <tr>
                        <td colSpan={6}>{t('noOpeningHoursFound', { ns: 'companies' })}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>

                {showOpeningHourForm && canManageBusinessHours ? (
                <div className="company-section" style={{ marginTop: 16 }}>
                  <div className="company-section-head">
                    <h3>{editingOpeningHourId ? t('editOpeningHour', { ns: 'companies' }) : t('createOpeningHour', { ns: 'companies' })}</h3>
                  </div>
                  <div className="company-form-grid">
                    <label>
                      {t('location', { ns: 'companies' })}
                      <select
                        className="text-input"
                        value={openingHourForm.locationId || ''}
                        onChange={event => setOpeningHourForm(prev => ({ ...prev, locationId: event.target.value || undefined }))}
                      >
                        <option value="">{t('allLocations', { ns: 'companies' })}</option>
                        {locations.map(location => (
                          <option key={location.id} value={location.id}>{location.name}</option>
                        ))}
                      </select>
                    </label>

                    <label>
                      {t('repeatPattern', { ns: 'companies' })}
                      <select
                        className="text-input"
                        value={openingHourForm.repeatPattern || 'weekly'}
                        onChange={event => setOpeningHourForm(prev => ({ ...prev, repeatPattern: event.target.value as ScheduleRepeatPattern }))}
                      >
                        {repeatPatternOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>

                    <label>
                      {t('startDate', { ns: 'companies' })}
                      <LocalizedDateInput
                        className="text-input"
                        displayFormat="long"
                        value={toDateInputValue(openingHourForm.startDate)}
                        onValueChange={(startDate) => setOpeningHourForm(prev => ({ ...prev, startDate: startDate || undefined }))}
                        fillWidth
                      />
                    </label>

                    <label>
                      {t('endDate', { ns: 'companies' })}
                      <LocalizedDateInput
                        className="text-input"
                        displayFormat="long"
                        value={toDateInputValue(openingHourForm.endDate)}
                        onValueChange={(endDate) => setOpeningHourForm(prev => ({ ...prev, endDate: endDate || undefined }))}
                        fillWidth
                      />
                    </label>

                    <label>
                      {t('startTime', { ns: 'companies' })}
                      <TimeInput24
                        className="text-input"
                        value={(openingHourForm.startTime || '').slice(0, 5)}
                        onChange={(startTime) => setOpeningHourForm(prev => ({
                          ...prev,
                          startTime: startTime ? `${startTime}:00` : undefined,
                        }))}
                        disabled={Boolean(openingHourForm.isAllDay)}
                      />
                    </label>

                    <label>
                      {t('endTime', { ns: 'companies' })}
                      <TimeInput24
                        className="text-input"
                        value={(openingHourForm.endTime || '').slice(0, 5)}
                        onChange={(endTime) => setOpeningHourForm(prev => ({
                          ...prev,
                          endTime: endTime ? `${endTime}:00` : undefined,
                        }))}
                        disabled={Boolean(openingHourForm.isAllDay)}
                      />
                    </label>

                    <label>
                      {t('isAllDay', { ns: 'companies' })}
                      <select
                        className="text-input"
                        value={openingHourForm.isAllDay ? 'yes' : 'no'}
                        onChange={event => setOpeningHourForm(prev => ({ ...prev, isAllDay: event.target.value === 'yes' }))}
                      >
                        <option value="no">{t('no', { ns: 'admin' })}</option>
                        <option value="yes">{t('yes', { ns: 'admin' })}</option>
                      </select>
                    </label>

                    <label>
                      {t('status', { ns: 'admin' })}
                      <select
                        className="text-input"
                        value={openingHourForm.isEnabled ? 'active' : 'inactive'}
                        onChange={event => setOpeningHourForm(prev => ({ ...prev, isEnabled: event.target.value === 'active' }))}
                      >
                        <option value="active">{t('active', { ns: 'admin' })}</option>
                        <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
                      </select>
                    </label>
                  </div>

                  <div className="company-toolbar" style={{ marginTop: 12, flexWrap: 'wrap' }}>
                    {dayOptions.map(option => (
                      <label key={option.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(openingHourForm.daysOfWeek?.includes(option.value))}
                          onChange={event => {
                            setOpeningHourForm(prev => {
                              const current = new Set(prev.daysOfWeek || []);
                              if (event.target.checked) {
                                current.add(option.value);
                              } else {
                                current.delete(option.value);
                              }

                              return { ...prev, daysOfWeek: Array.from(current).sort((left, right) => left - right) };
                            });
                          }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>

                  <div className="company-actions-inline">
                    <button type="button" className="btn btn-primary" onClick={saveOpeningHour} disabled={savingOpeningHour}>
                      {savingOpeningHour ? t('savingEllipsis', { ns: 'admin' }) : editingOpeningHourId ? t('updateOpeningHour', { ns: 'companies' }) : t('createOpeningHour', { ns: 'companies' })}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={cancelOpeningHourForm} disabled={savingOpeningHour}>
                      {editingOpeningHourId ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
                    </button>
                  </div>
                </div>
                ) : null}
              </>
            )}
          </section>

          <section className="company-section">
            <div className="company-section-head">
              <h3>{t('closures', { ns: 'companies' })}</h3>
              {canManageBusinessHours ? (
                <button type="button" className="btn btn-secondary" onClick={startCreateClosure} disabled={!selectedCompanyId}>{t('newClosure', { ns: 'companies' })}</button>
              ) : null}
            </div>

            {closureError ? <div className="error">{closureError}</div> : null}

            {!selectedCompanyId ? (
              <div>{t('selectCompanyFirst', { ns: 'companies' })}</div>
            ) : loadingClosures ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <Spinner text={t('loadingClosures', { ns: 'companies' })} />
              </div>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>{t('location', { ns: 'companies' })}</th>
                      <th>{t('repeat', { ns: 'companies' })}</th>
                      <th>{t('range', { ns: 'companies' })}</th>
                      <th>{t('status', { ns: 'admin' })}</th>
                      <th>{t('actions', { ns: 'admin' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closures.map(closure => (
                      <tr key={closure.id}>
                        <td>{locations.find(location => location.id === closure.locationId)?.name || t('allLocations', { ns: 'companies' })}</td>
                        <td>{repeatPatternOptions.find(option => option.value === closure.repeatPattern)?.label || closure.repeatPattern}</td>
                        <td>{closure.isAllDay ? t('allDay', { ns: 'companies' }) : `${formatLocalizedDateTimeLabel(closure.startAtUtc || '')} - ${formatLocalizedDateTimeLabel(closure.endAtUtc || '')}`}</td>
                        <td>
                          <span className={`company-status-pill ${closure.isEnabled ? '' : 'is-inactive'}`}>
                            {closure.isEnabled ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}
                          </span>
                        </td>
                        <td>
                          <div className="company-actions-inline">
                            {canManageBusinessHours ? (
                              <>
                                <button type="button" className="btn btn-secondary" onClick={() => startEditClosure(closure)}>{t('edit', { ns: 'common' })}</button>
                                <button type="button" className="btn btn-secondary btn-danger-subtle" onClick={() => removeClosure(closure.id)} disabled={savingClosure}>{t('delete', { ns: 'common' })}</button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {closures.length === 0 ? (
                      <tr>
                        <td colSpan={5}>{t('noClosuresFound', { ns: 'companies' })}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>

                {showClosureForm && canManageBusinessHours ? (
                <div className="company-section" style={{ marginTop: 16 }}>
                  <div className="company-section-head">
                    <h3>{editingClosureId ? t('editClosure', { ns: 'companies' }) : t('createClosure', { ns: 'companies' })}</h3>
                  </div>

                  <div className="company-form-grid">
                    <label>
                      {t('location', { ns: 'companies' })}
                      <select
                        className="text-input"
                        value={closureForm.locationId || ''}
                        onChange={event => setClosureForm(prev => ({ ...prev, locationId: event.target.value || undefined }))}
                      >
                        <option value="">{t('allLocations', { ns: 'companies' })}</option>
                        {locations.map(location => (
                          <option key={location.id} value={location.id}>{location.name}</option>
                        ))}
                      </select>
                    </label>

                    <label>
                      {t('repeatPattern', { ns: 'companies' })}
                      <select
                        className="text-input"
                        value={closureForm.repeatPattern || 'none'}
                        onChange={event => setClosureForm(prev => ({ ...prev, repeatPattern: event.target.value as ScheduleRepeatPattern }))}
                      >
                        {repeatPatternOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>

                    <label>
                      {t('startDateTime', { ns: 'companies' })}
                      <LocalizedDateTimeInput
                        value={utcToLocalDateTimeInput(closureForm.startAtUtc)}
                        onValueChange={(value) => setClosureForm(prev => ({
                          ...prev,
                          startAtUtc: localDateTimeInputToUtc(value),
                        }))}
                      />
                    </label>

                    <label>
                      {t('endDateTime', { ns: 'companies' })}
                      <LocalizedDateTimeInput
                        value={utcToLocalDateTimeInput(closureForm.endAtUtc)}
                        onValueChange={(value) => setClosureForm(prev => ({
                          ...prev,
                          endAtUtc: localDateTimeInputToUtc(value),
                        }))}
                      />
                    </label>

                    <label>
                      {t('isAllDay', { ns: 'companies' })}
                      <select
                        className="text-input"
                        value={closureForm.isAllDay ? 'yes' : 'no'}
                        onChange={event => setClosureForm(prev => ({ ...prev, isAllDay: event.target.value === 'yes' }))}
                      >
                        <option value="no">{t('no', { ns: 'admin' })}</option>
                        <option value="yes">{t('yes', { ns: 'admin' })}</option>
                      </select>
                    </label>

                    <label>
                      {t('status', { ns: 'admin' })}
                      <select
                        className="text-input"
                        value={closureForm.isEnabled ? 'active' : 'inactive'}
                        onChange={event => setClosureForm(prev => ({ ...prev, isEnabled: event.target.value === 'active' }))}
                      >
                        <option value="active">{t('active', { ns: 'admin' })}</option>
                        <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
                      </select>
                    </label>
                  </div>

                  <div className="company-actions-inline">
                    <button type="button" className="btn btn-primary" onClick={saveClosure} disabled={savingClosure}>
                      {savingClosure ? t('savingEllipsis', { ns: 'admin' }) : editingClosureId ? t('updateClosure', { ns: 'companies' }) : t('createClosure', { ns: 'companies' })}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={cancelClosureForm} disabled={savingClosure}>
                      {editingClosureId ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
                    </button>
                  </div>
                </div>
                ) : null}
              </>
            )}
          </section>
        </>
      ) : null}

      {activeTab === 'chatbot' ? (
        <>
          {hasBroadCompanyAccess && !canManageAssistantSettings ? (
            <div className="company-tab-readonly-hint company-tab-readonly-hint--info" role="status">
              {t('chatbotAssistantViewRestrictedHint', { ns: 'companies' })}
            </div>
          ) : null}
          {selectedCompanyId && canManageAssistantSettings ? (
            <section className="company-section">
              <div className="company-section-head">
                <h3>{t('assistantSettingsHeading', { ns: 'companies' })}</h3>
              </div>

              <p style={{ color: '#516173', marginTop: 0 }}>
                {t('assistantSettingsDescription', { ns: 'companies' })}
              </p>

              {assistantSettingsError ? <div className="error">{assistantSettingsError}</div> : null}
              {assistantSettingsNotice ? <div className="success">{assistantSettingsNotice}</div> : null}

              {loadingAssistantSettings ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                  <Spinner text={t('loadingAssistantSettings', { ns: 'companies' })} />
                </div>
              ) : (
                <>
                  <div className="company-form-grid assistant-settings-grid">
                    <label>
                      {t('assistantDisplayName', { ns: 'companies' })}
                      <input
                        className="text-input"
                        value={assistantSettingsForm.displayName || ''}
                        onChange={event => setAssistantSettingsForm(prev => ({ ...prev, displayName: event.target.value }))}
                        placeholder="AI Assistant"
                      />
                    </label>

                    <label>
                      {t('assistantMemoryRefreshThreshold', { ns: 'companies' })}
                      <input
                        className="text-input"
                        type="number"
                        min={1}
                        max={100}
                        value={assistantSettingsForm.autoRefreshAiMemoryMessageThreshold ?? ''}
                        onChange={event => {
                          const nextValue = event.target.value;
                          setAssistantSettingsForm(prev => ({
                            ...prev,
                            autoRefreshAiMemoryMessageThreshold: nextValue === ''
                              ? undefined
                              : Number.parseInt(nextValue, 10),
                          }));
                        }}
                        placeholder="5"
                      />
                    </label>

                    <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(assistantSettingsForm.isBackgroundMemoryRefreshEnabled ?? true)}
                        onChange={event => setAssistantSettingsForm(prev => ({
                          ...prev,
                          isBackgroundMemoryRefreshEnabled: event.target.checked,
                        }))}
                      />
                      <div>{t('assistantMemoryRefreshEnabled', { ns: 'companies' })}</div>
                    </label>

                    <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8, gridColumn: '1 / -1' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(assistantSettingsForm.showEmployeeNameOnExternalOutbound)}
                        onChange={event => setAssistantSettingsForm(prev => ({
                          ...prev,
                          showEmployeeNameOnExternalOutbound: event.target.checked,
                        }))}
                      />
                      <div>{t('assistantShowEmployeeNameOnExternalOutbound', { ns: 'companies' })}</div>
                    </label>

                    <label style={{ gridColumn: '1 / -1' }}>
                      {t('assistantGeneratedMessageDisclaimer', { ns: 'companies' })}
                      <textarea
                        className="text-input"
                        rows={4}
                        maxLength={500}
                        value={assistantSettingsForm.aiGeneratedMessageDisclaimer || ''}
                        onChange={event => setAssistantSettingsForm(prev => ({
                          ...prev,
                          aiGeneratedMessageDisclaimer: event.target.value,
                        }))}
                      />
                      <span style={{ color: '#516173', fontWeight: 500, fontSize: '0.9rem' }}>
                        {t('assistantGeneratedMessageDisclaimerHelp', { ns: 'companies' })}
                      </span>
                    </label>

                    <div className="company-actions-inline" style={{ gridColumn: '1 / -1' }}>
                      <button type="button" className="btn btn-primary" onClick={saveAssistantSettings} disabled={savingAssistantSettings}>
                        {savingAssistantSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveAssistantSettings', { ns: 'companies' })}
                      </button>
                    </div>
                  </div>

                  <div
                    role="presentation"
                    style={{
                      marginTop: 20,
                      marginBottom: 4,
                      borderTop: '1px solid #dbe3ee',
                    }}
                  />

                  <AssistantCommentReplyModePanel
                    settings={assistantCommentReplySettings}
                    onSettingsChange={setAssistantCommentReplySettings}
                    onNotice={message => {
                      setAssistantSettingsNotice(message);
                      showToast(message, 'success');
                    }}
                    onError={message => setAssistantSettingsError(message)}
                  />

                  <div className="assistant-mute-advanced-callout">
                    <div className="assistant-mute-advanced-callout-copy">
                      <h4 className="assistant-mute-advanced-callout-title">
                        {t('assistantMuteAdvancedSettingsTitle', { ns: 'companies' })}
                      </h4>
                      <p className="assistant-mute-advanced-callout-description">
                        {t('assistantMuteAdvancedSettingsCallout', { ns: 'companies' })}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary assistant-mute-advanced-callout-button"
                      onClick={() => setAssistantMuteAdvancedOpen(true)}
                    >
                      {t('assistantMuteAdvancedSettingsOpenButton', { ns: 'companies' })}
                    </button>
                  </div>

                  <AssistantAiMuteAdvancedSettingsModal
                    isOpen={assistantMuteAdvancedOpen}
                    onClose={() => setAssistantMuteAdvancedOpen(false)}
                    onNotice={message => {
                      setAssistantSettingsNotice(message);
                      showToast(message, 'success');
                    }}
                    onError={message => setAssistantSettingsError(message)}
                  />

                  {canManageTokenBudget ? <AiTokenBudgetPanel /> : null}

                  <AssistantVoiceSettingsPanel />

                </>
              )}
            </section>
          ) : null}

          <section className="company-section">
            <div className="company-section-head">
              <h3>{t('chatbotBlockoutsHeading', { ns: 'companies' })}</h3>
              <p style={{ margin: '6px 0 0', color: '#4b5563', fontSize: 13 }}>
                {t('chatbotBookingRulesHint', { ns: 'companies' })}
              </p>
            </div>

            {!canManageChatbotBlockouts ? <CompanyTabReadOnlyBanner /> : null}

            {chatBotError ? <div className="error">{chatBotError}</div> : null}

            {!selectedCompanyId ? (
              <div>{t('selectCompanyFirst', { ns: 'companies' })}</div>
            ) : loadingChatBotBlockouts ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <Spinner text={t('loadingChatbotBlockouts', { ns: 'companies' })} />
              </div>
            ) : (
              <div className="company-table-scroll">
              <table>
              <thead>
                <tr>
                  <th>{t('name', { ns: 'admin' })}</th>
                  <th>{t('consultationTypeColumn', { ns: 'companies' })}</th>
                  <th>{t('ruleModeColumn', { ns: 'companies' })}</th>
                  <th>{t('appliesToColumn', { ns: 'companies' })}</th>
                  <th>{t('days', { ns: 'companies' })}</th>
                  <th>{t('roomsColumn', { ns: 'companies' })}</th>
                  <th>{t('active', { ns: 'admin' })}</th>
                  <th>{t('time', { ns: 'companies' })}</th>
                  <th>{t('actions', { ns: 'admin' })}</th>
                </tr>
              </thead>
              <tbody>
                {chatBotBlockouts.map(blockout => {
                  const draft = chatBotDraftById[blockout.id] || toChatBotBlockoutForm(blockout);
                  const hasPendingRowChanges = hasChatBotRowChanges(blockout, draft);
                  const selectedRoomIds = normalizeChatBotDraftRoomIds(draft.roomIds);
                  const availableRooms = chatBotRoomCatalog.filter(room => !selectedRoomIds.includes(room.id));

                  return (
                    <React.Fragment key={blockout.id}>
                    <tr style={blockout.hasConflict ? { background: '#fef2f2' } : undefined}>
                      <td>
                        <input
                          className="text-input"
                          value={draft.name || ''}
                          disabled={!canManageChatbotBlockouts}
                          onChange={event => setChatBotDraft(blockout.id, prev => ({ ...prev, name: event.target.value }))}
                        />
                      </td>
                      <td>
                        <select
                          className="text-input"
                          value={draft.appointmentTypeId || ''}
                          disabled={!canManageChatbotBlockouts}
                          onChange={event => setChatBotDraft(blockout.id, prev => ({
                            ...prev,
                            appointmentTypeId: event.target.value || undefined,
                          }))}
                        >
                          <option value="">{t('allConsultTypes', { ns: 'companies' })}</option>
                          {chatBotAppointmentTypes.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="text-input"
                          value={draft.mode || 'never'}
                          disabled={!canManageChatbotBlockouts}
                          onChange={event => setChatBotDraft(blockout.id, prev => ({
                            ...prev,
                            mode: event.target.value as BookingPreferenceRuleMode,
                          }))}
                        >
                          <option value="never">{t('ruleModeNever', { ns: 'companies' })}</option>
                          <option value="limitedTo">{t('ruleModeLimitedTo', { ns: 'companies' })}</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="text-input"
                          value={draft.appliesTo || 'aiAssistant'}
                          disabled={!canManageChatbotBlockouts}
                          onChange={event => setChatBotDraft(blockout.id, prev => ({
                            ...prev,
                            appliesTo: event.target.value as BookingPreferenceAppliesTo,
                          }))}
                        >
                          <option value="aiAssistant">{t('appliesToAiAssistant', { ns: 'companies' })}</option>
                          <option value="general">{t('appliesToGeneral', { ns: 'companies' })}</option>
                        </select>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: 280 }}>
                          {dayOptions.map(option => (
                            <label key={`${blockout.id}-${option.value}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="checkbox"
                                checked={Boolean(draft.daysOfWeek?.includes(option.value))}
                                disabled={!canManageChatbotBlockouts}
                                onChange={event => {
                                  setChatBotDraft(blockout.id, prev => {
                                    const current = new Set(prev.daysOfWeek || []);
                                    if (event.target.checked) {
                                      current.add(option.value);
                                    } else {
                                      current.delete(option.value);
                                    }

                                    return { ...prev, daysOfWeek: Array.from(current).sort((left, right) => left - right) };
                                  });
                                }}
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {selectedRoomIds.length === 0 ? (
                            <span style={{ color: '#6b7280' }}>{t('allRooms', { ns: 'companies' })}</span>
                          ) : selectedRoomIds.map(roomId => {
                            const roomMeta = chatBotRoomCatalog.find(room => room.id === roomId);
                            return (
                              <span key={roomId} style={{ border: '1px solid #dce3ec', borderRadius: 12, padding: '2px 8px', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                {roomMeta?.roomName || roomId}
                                {canManageChatbotBlockouts ? (
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '0 6px', lineHeight: 1.1 }}
                                    onClick={() => removeRoomFromInlineBlockoutDraft(blockout.id, roomId)}
                                  >
                                    x
                                  </button>
                                ) : null}
                              </span>
                            );
                          })}
                        </div>
                        {canManageChatbotBlockouts ? (
                          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <select
                              className="text-input"
                              value={chatBotRoomPickerByBlockout[blockout.id] || ''}
                              onChange={event => setChatBotRoomPickerByBlockout(prev => ({ ...prev, [blockout.id]: event.target.value }))}
                              disabled={availableRooms.length === 0}
                            >
                              <option value="">{t('addRoomPlaceholder', { ns: 'companies' })}</option>
                              {availableRooms.map(room => (
                                <option key={room.id} value={room.id}>{room.locationName} / {room.roomGroupName} / {room.roomName}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => addRoomToInlineBlockoutDraft(blockout.id)}
                              disabled={!chatBotRoomPickerByBlockout[blockout.id]}
                            >
                              {t('add', { ns: 'common' })}
                            </button>
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={draft.isEnabled ?? true}
                          disabled={!canManageChatbotBlockouts}
                          onChange={event => setChatBotDraft(blockout.id, prev => ({ ...prev, isEnabled: event.target.checked }))}
                        />
                      </td>
                      <td>
                        <ChatBotScheduleFields
                          draft={draft}
                          onDraftChange={(updater) => setChatBotDraft(blockout.id, updater)}
                          disabled={!canManageChatbotBlockouts}
                        />
                      </td>
                      <td>
                        <div className="company-actions-inline">
                          {canManageChatbotBlockouts ? (
                            <>
                              {hasPendingRowChanges ? (
                                <button type="button" className="btn btn-primary" onClick={() => saveInlineChatBotBlockout(blockout.id)} disabled={savingChatBotBlockout}>{t('save', { ns: 'admin' })}</button>
                              ) : null}
                              <button type="button" className="btn btn-secondary btn-danger-subtle" onClick={() => removeChatBotBlockout(blockout.id)} disabled={savingChatBotBlockout}>{t('delete', { ns: 'common' })}</button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {blockout.hasConflict ? (
                      <tr style={{ background: '#fef2f2' }}>
                        <td colSpan={9} style={{ color: '#b91c1c', fontSize: 13, paddingTop: 0 }}>
                          {t('bookingRuleConflictWarning', { ns: 'companies' })}
                        </td>
                      </tr>
                    ) : null}
                    </React.Fragment>
                  );
                })}

                <tr>
                  <td>
                    <input
                      className="text-input"
                      placeholder={t('newBlockoutName', { ns: 'companies' })}
                      value={newChatBotBlockoutForm.name || ''}
                      onChange={event => setNewChatBotBlockoutForm(prev => ({ ...prev, name: event.target.value }))}
                    />
                  </td>
                  <td>
                    <select
                      className="text-input"
                      value={newChatBotBlockoutForm.appointmentTypeId || ''}
                      onChange={event => setNewChatBotBlockoutForm(prev => ({
                        ...prev,
                        appointmentTypeId: event.target.value || undefined,
                      }))}
                    >
                      <option value="">{t('allConsultTypes', { ns: 'companies' })}</option>
                      {chatBotAppointmentTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="text-input"
                      value={newChatBotBlockoutForm.mode || 'never'}
                      onChange={event => setNewChatBotBlockoutForm(prev => ({
                        ...prev,
                        mode: event.target.value as BookingPreferenceRuleMode,
                      }))}
                    >
                      <option value="never">{t('ruleModeNever', { ns: 'companies' })}</option>
                      <option value="limitedTo">{t('ruleModeLimitedTo', { ns: 'companies' })}</option>
                    </select>
                  </td>
                  <td>
                    <select
                      className="text-input"
                      value={newChatBotBlockoutForm.appliesTo || 'aiAssistant'}
                      onChange={event => setNewChatBotBlockoutForm(prev => ({
                        ...prev,
                        appliesTo: event.target.value as BookingPreferenceAppliesTo,
                      }))}
                    >
                      <option value="aiAssistant">{t('appliesToAiAssistant', { ns: 'companies' })}</option>
                      <option value="general">{t('appliesToGeneral', { ns: 'companies' })}</option>
                    </select>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {dayOptions.map(option => (
                        <label key={`new-${option.value}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={Boolean(newChatBotBlockoutForm.daysOfWeek?.includes(option.value))}
                            onChange={event => {
                              setNewChatBotBlockoutForm(prev => {
                                const current = new Set(prev.daysOfWeek || []);
                                if (event.target.checked) {
                                  current.add(option.value);
                                } else {
                                  current.delete(option.value);
                                }
                                return { ...prev, daysOfWeek: Array.from(current).sort((left, right) => left - right) };
                              });
                            }}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(newChatBotBlockoutForm.roomIds || []).length === 0 ? (
                        <span style={{ color: '#6b7280' }}>{t('allRooms', { ns: 'companies' })}</span>
                      ) : (newChatBotBlockoutForm.roomIds || []).map(roomId => {
                        const roomMeta = chatBotRoomCatalog.find(room => room.id === roomId);
                        return (
                          <span key={roomId} style={{ border: '1px solid #dce3ec', borderRadius: 12, padding: '2px 8px', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            {roomMeta?.roomName || roomId}
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ padding: '0 6px', lineHeight: 1.1 }}
                              onClick={() => setNewChatBotBlockoutForm(prev => ({
                                ...prev,
                                roomIds: normalizeChatBotDraftRoomIds((prev.roomIds || []).filter(id => id !== roomId)),
                              }))}
                            >
                              x
                            </button>
                          </span>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <select
                        className="text-input"
                        value={newChatBotRoomPicker}
                        onChange={event => setNewChatBotRoomPicker(event.target.value)}
                      >
                        <option value="">{t('addRoomPlaceholder', { ns: 'companies' })}</option>
                        {chatBotRoomCatalog
                          .filter(room => !(newChatBotBlockoutForm.roomIds || []).includes(room.id))
                          .map(room => (
                            <option key={room.id} value={room.id}>{room.locationName} / {room.roomGroupName} / {room.roomName}</option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={addRoomToNewBlockoutDraft}
                        disabled={!newChatBotRoomPicker}
                      >
                        {t('add', { ns: 'common' })}
                      </button>
                    </div>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={newChatBotBlockoutForm.isEnabled ?? true}
                      onChange={event => setNewChatBotBlockoutForm(prev => ({ ...prev, isEnabled: event.target.checked }))}
                    />
                  </td>
                  <td>
                    <ChatBotScheduleFields
                      draft={newChatBotBlockoutForm}
                      onDraftChange={(updater) => setNewChatBotBlockoutForm(prev => updater(prev))}
                      disabled={savingChatBotBlockout}
                    />
                  </td>
                  <td>
                    {!areChatBotDraftsEqual(newChatBotBlockoutForm, emptyChatBotBlockoutForm) && canManageChatbotBlockouts ? (
                      <button type="button" className="btn btn-secondary" onClick={createInlineChatBotBlockout} disabled={savingChatBotBlockout}>{t('add', { ns: 'common' })}</button>
                    ) : null}
                  </td>
                </tr>
              </tbody>
              </table>
              </div>
            )}
          </section>
        </>
      ) : null}
      </div>

      <DepartmentModal
        isOpen={showDepartmentForm && activeTab === 'departments' && canManageDepartments}
        isEditing={Boolean(editingDepartmentId)}
        form={departmentForm}
        parentOptions={parentDepartmentOptions}
        saving={savingDepartment}
        error={departmentError}
        onClose={cancelDepartmentForm}
        onChange={setDepartmentForm}
        onSave={() => void saveDepartment()}
      />

      <PositionModal
        isOpen={showPositionForm && activeTab === 'positions' && canManagePositions}
        isEditing={Boolean(editingPositionId)}
        form={positionForm}
        departmentOptions={positionDepartmentOptions}
        saving={savingPosition}
        error={positionError}
        onClose={cancelPositionForm}
        onChange={setPositionForm}
        onSave={() => void savePosition()}
      />

    </div>
  );
};

export default CompaniesPage;
